import React, { useEffect, useMemo, useRef, useState } from "react";
import { getMonthCellLimits } from "./monthCalendarLayout";
import { Loader, PageOverlayLoader, StepUpSpinner } from "../../../Components/Loader";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Checkbox,
  Collapse,
  Stack,
  LinearProgress,
  Avatar,
  CircularProgress,
  Popover,
  Skeleton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  useGetClassScheduleQuery,
  useGetClassScheduleSummaryQuery,
  useSwitchClassStaffMutation,
  useCheckStaffAvailabilityMutation,
  useReleaseClassMutation,
  useGetTeamQuery,
  useGetClassScheduleFilterOptionsQuery,
  useGetStaffPortalClassSessionsFilterOptionsQuery,
  useGetallLocationQuery,
  useGetallSubjectQuery,
  useGetallGradeQuery,
} from "../../../Api/attoDeskApi";
import { useConfirm } from "../../../Components/Confirm";
import { useNotifier } from "../../../Core/Notifier";
import useUser from "../../../Core/hook/useUser";
import { Privileges } from "../../../Core/Enum/enum";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import Pagination from "../../../Components/Pagination";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import { normalizeStaffOptions, normalizeStudentOptions, normalizeGradeOptions, normalizeSubjectOptions, subjectsForGrades } from "./filterOptions";
import { StudentProfileLink } from "../../../Components/ProfileLinks";
import { useStudentProfilePath } from "../../StaffPortal/shared/routes";
import SubjectMultiSelect from "./SubjectMultiSelect";
import GradeMultiSelect from "./GradeMultiSelect";
import StaffMultiSelect from "./StaffMultiSelect";
import DayMultiSelect from "./DayMultiSelect";
import { normalizeDays, parseDaysParam, serializeDays, hhmm, fmt12, dayLabel, isTimeRangeValid, rowMatchesDayTime } from "./dayTimeFilter";
import { statusMetaFor, RECURRING_ACCENT } from "./statusMeta";
import { DayView, WeekView } from "./TimelineViews";
import DuplicateDialog from "./DuplicateDialog";
import AssignStudentDialog from "./AssignStudentDialog";
import BulkSwitchStaffDialog from "./BulkSwitchStaffDialog";
import SwitchStaffDialog from "./SwitchStaffDialog";
import ScheduleDetailsDrawer from "./ScheduleDetailsDrawer";
import MonthEventCard from "./MonthEventCard";
import LegendBar from "./LegendBar";
import ExpandedScheduleDetails from "./ExpandedScheduleDetails";
import MakeupWorkflow from "./MakeupWorkflow";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import ReplacementStaffPicker from "./ReplacementStaffPicker";
import LocationLegend from "../../../Components/LocationLegend";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import AddIcon from "@mui/icons-material/Add";
import SelectStudentDialog from "./SelectStudentDialog";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ChecklistIcon from "@mui/icons-material/Checklist";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ViewDayOutlinedIcon from "@mui/icons-material/ViewDayOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import RepeatIcon from "@mui/icons-material/Repeat";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import SearchOffIcon from "@mui/icons-material/SearchOff";

const VIEW_KEY = "cs_view_mode";

// ── Filter toolbar layout ──
// Two explicit rows with breakpoint-driven column counts. `auto-fit` was
// producing ragged gaps and squeezed fields at 1180/1024, so each row now has
// a deterministic template per breakpoint. Every cell keeps a floor width
// (minmax min) that prevents labels/values from clipping.
//
// Row 1 (desktop): Search · From · To · Staff · Student · Location · Grades · Subjects
// Row 2 (desktop): Mode · Status · Enrollment · Class Type · Days · Start · End · Reset
const FILTER_GRID_ROW1_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
    // The two date columns (From / To) previously floored at 140px, which is
    // 6-10px short of what MM/DD/YYYY + calendar-icon adornment + outlined
    // padding actually needs; the value clipped under the icon at typical
    // desktop widths. Raised to 170px so the intrinsic content fits with
    // breathing room. Fr share unchanged so column proportions across Row 1
    // are visually stable — only the minimum floor changed.
    xl: "minmax(240px, 1.8fr) repeat(2, minmax(170px, 0.85fr)) repeat(5, minmax(135px, 1fr))",
  },
  gap: 1.5,           // 12px — matches the 10–12px spec
  rowGap: 1.5,
  alignItems: "center",
} as const;

const FILTER_GRID_ROW2_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
    xl: "repeat(8, minmax(0, 1fr))",
  },
  gap: 1.5,
  rowGap: 1.5,
  alignItems: "center",
} as const;

// Applied to the grid CONTAINER so every control inside it — TextField, Select,
// Autocomplete, DatePicker, the multi-selects and PremiumTimePicker — lands on
// the same height and radius, whatever component renders it. Menus/popovers are
// portalled out of this subtree, so their internals are unaffected.
const FILTER_FIELD_SX = {
  "& .MuiInputBase-root": { minHeight: 44, borderRadius: "10px" },
  // Every field control fills its grid cell, so column widths (not the control
  // itself) decide layout. Prevents the "collapsed" look at 1180/1024.
  "& .MuiFormControl-root": { width: "100%" },
  "& .MuiTextField-root": { width: "100%" },
  "& .MuiAutocomplete-root": { width: "100%" },
  // Autocomplete packs its input; keep the outer control on the same rhythm.
  "& .MuiAutocomplete-inputRoot": { paddingTop: "2px", paddingBottom: "2px" },
  // Grid children must be allowed to shrink or long labels force an overflow.
  "& > *": { minWidth: 0 },
} as const;

// ?from_date=YYYY-MM-DD / ?to_date=YYYY-MM-DD → Dayjs, or null when absent or
// malformed. The shape is checked with a regex rather than dayjs strict parsing,
// which would need the customParseFormat plugin (only extended inside the
// DatePicker helper — relying on that import order here would be fragile).
//
// dayjs("YYYY-MM-DD") parses as LOCAL midnight, unlike `new Date(str)` which
// reads a bare date as UTC and can land a day early west of Greenwich.
const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;
const parseDateParam = (raw: string | null): Dayjs | null => {
  if (!raw || !DATE_PARAM_RE.test(raw)) return null;
  const d = dayjs(raw);
  return d.isValid() ? d.startOf("day") : null;
};

// ── Single source of truth for the desktop table grid ──
// Header, data rows, expanded rows and skeleton rows all consume THIS value so
// header/body column boundaries can never be computed independently. minmax(min,fr)
// keeps every column at a stable minimum (no merging) while still flexing, and the
// px minimums for Students/Location/Mode/Status give each cell its own clear lane.
//        select   date   time   class                staff                students             location             mode                 status               actions
const GRID_TEMPLATE_COLUMNS =
  "42px 92px 104px minmax(190px, 1.7fr) minmax(150px, 1.3fr) minmax(195px, 1.25fr) minmax(140px, 0.9fr) minmax(118px, 0.75fr) minmax(118px, 0.8fr) 56px";
// Below this the grid horizontally scrolls inside its container instead of
// compressing Students/Location into each other.
const GRID_MIN_WIDTH = 1140;

// POST /class-schedule/getAll returns `scheduledDate` as "YYYY-MM-DD".
const rawScheduleDate = (row: any): string | null =>
  row?.scheduledDate ?? row?.class_date ?? row?.scheduled_date ?? row?.date ?? null;

const formatScheduleDate = (row: any, pattern = "MMM DD, YYYY") => {
  const raw = rawScheduleDate(row);
  if (!raw) return "—";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format(pattern) : String(raw);
};

const scheduleDateKey = (row: any) => {
  const raw = rawScheduleDate(row);
  if (!raw) return "";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(raw).slice(0, 10);
};

const formatTime = (time: string | undefined) => {
  if (!time) return "";
  const [hour, minute] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hour, minute);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(date);
};

const initials = (name: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("") || "?";

const modeLabel = (m: string) => (m === "Online" ? "Online" : m === "InPerson" || m === "Offline" ? "In Person" : m || "—");
// HH:mm ↔ minutes (used to preserve makeup duration when the start time changes).
const toMin = (t: string) => { const [h, m] = String(t || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const minToHHmm = (mins: number) => { const x = ((mins % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };

// `week_timetable` is the new day-grouped agenda-style layout (the default
// landing view). `week` is the older horizontal WeekView grid, kept for
// admins who still prefer that shape. Every existing mode remains
// selectable so no user loses functionality on upgrade.
// ─── Week Timetable view ──────────────────────────────────────────────
// Day-grouped agenda. Rows for the selected Mon..Sun window are bucketed
// by day-of-week, then by (start–end) time range so identical slots are
// listed once with all classes underneath. Today is expanded by default;
// empty days collapse to a "No classes" row so the eye can skip them.
// Clicking a row fires the existing action-menu handler — the details
// drawer / action list is already wired up at the page level, so this
// component is deliberately dumb about actions.
type TimetableRowProps = {
  rows: any[];
  weekStart: Dayjs;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onPickDate: (d: Dayjs) => void;
  onEventClick: (e: React.MouseEvent<HTMLElement>, row: any) => void;
};

const WeekTimetableView: React.FC<TimetableRowProps> = ({
  rows, weekStart, onPrev, onNext, onToday, onPickDate, onEventClick,
}) => {
  const theme = useTheme();
  const todayKey = dayjs().format("YYYY-MM-DD");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [pickerOpen, setPickerOpen] = useState(false);

  // Build the 7 day buckets in Mon..Sun order regardless of what dates
  // the input rows happen to carry — a blank Wednesday still renders.
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")), [weekStart]);

  const byDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    rows.forEach((r) => {
      const k = scheduleDateKey(r);
      if (!k) return;
      (map[k] = map[k] || []).push(r);
    });
    return map;
  }, [rows]);

  // Within a day, group by "HH:MM-HH:MM" so identical time slots share
  // one heading. Sort by start-time ascending.
  const groupByTime = (dayRows: any[]) => {
    const map = new Map<string, any[]>();
    dayRows.forEach((r) => {
      const key = `${r.startTime || "00:00"}-${r.endTime || "00:00"}`;
      const bucket = map.get(key) || [];
      bucket.push(r);
      map.set(key, bucket);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  };

  const weekLabel = `${weekStart.format("MMMM D")} – ${weekStart.add(6, "day").format("MMMM D, YYYY")}`;

  return (
    <Box>
      {/* Date-range header */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1, mb: 2,
        flexWrap: "wrap",
      }}>
        <IconButton size="small" onClick={onPrev} aria-label="Previous week"><ChevronLeftIcon /></IconButton>
        <Button size="small" variant="outlined" onClick={onToday}
          sx={{ textTransform: "none", borderRadius: "8px" }}>
          Today
        </Button>
        <IconButton size="small" onClick={onNext} aria-label="Next week"><ChevronRightIcon /></IconButton>
        <Typography sx={{ fontSize: 15, fontWeight: 800, ml: 1 }}>{weekLabel}</Typography>
        <Box sx={{ flex: 1 }} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            open={pickerOpen}
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            value={weekStart}
            onChange={(v) => v && onPickDate(v)}
            slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
          />
        </LocalizationProvider>
      </Box>

      {/* Days */}
      <Box sx={{
        display: "grid", gap: 1.5,
        // Multi-column on wide screens; single column on tablet+mobile
        // so each day accordion has room for time headings and rows.
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
          xl: "repeat(3, minmax(0, 1fr))",
        },
      }}>
        {days.map((day) => {
          const key = day.format("YYYY-MM-DD");
          const isToday = key === todayKey;
          const dayRows = byDay[key] || [];
          const timeGroups = groupByTime(dayRows);
          // Today expanded by default. Other days: expanded unless the
          // user explicitly collapsed them. Empty days start collapsed
          // because there's nothing to reveal.
          const userCollapse = collapsed[key];
          const isCollapsed = userCollapse === undefined
            ? (!isToday && dayRows.length === 0)
            : userCollapse;

          return (
            <Box key={key} sx={{
              border: "1px solid rgba(15,23,42,0.08)",
              borderRadius: "12px",
              background: "#fff",
              overflow: "hidden",
            }}>
              <Box
                onClick={() => setCollapsed((p) => ({ ...p, [key]: !isCollapsed }))}
                sx={{
                  px: 1.75, py: 1.25,
                  display: "flex", alignItems: "center", gap: 1,
                  cursor: "pointer",
                  background: isToday
                    ? alpha(theme.palette.primary.main, 0.06)
                    : "#f8fafc",
                  borderBottom: isCollapsed ? "none" : "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <Typography sx={{
                  fontWeight: 800, fontSize: 13,
                  color: isToday ? theme.palette.primary.main : "#0f172a",
                }}>
                  {day.format("dddd")}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                  {day.format("MMM D")}
                </Typography>
                {isToday && (
                  <Chip label="Today" size="small" sx={{
                    height: 18, fontSize: 10, fontWeight: 700,
                    bgcolor: theme.palette.primary.main, color: "#fff",
                  }} />
                )}
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  label={dayRows.length === 0 ? "No classes" : `${dayRows.length} class${dayRows.length === 1 ? "" : "es"}`}
                  sx={{
                    height: 20, fontSize: 11, fontWeight: 600,
                    bgcolor: dayRows.length === 0 ? "#f1f5f9" : "#ecfdf5",
                    color:   dayRows.length === 0 ? "#64748b" : "#047857",
                  }}
                />
                <IconButton size="small">
                  {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" sx={{ transform: "rotate(-90deg)" }} />}
                </IconButton>
              </Box>
              {!isCollapsed && (
                dayRows.length === 0 ? (
                  <Box sx={{ px: 1.75, py: 1.5 }}>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No classes scheduled.</Typography>
                  </Box>
                ) : (
                  <Box>
                    {timeGroups.map(([timeKey, groupRows]) => {
                      const [s, e] = timeKey.split("-");
                      const timeLabel = `${formatTime(s)} – ${formatTime(e)}`;
                      return (
                        <Box key={timeKey} sx={{ borderTop: "1px solid rgba(15,23,42,0.04)" }}>
                          <Typography sx={{
                            px: 1.75, pt: 1, pb: 0.5,
                            fontSize: 12.5, fontWeight: 800, color: "#0f172a",
                            letterSpacing: 0.2,
                          }}>
                            {timeLabel}
                          </Typography>
                          {groupRows.map((row) => (
                            <Box
                              key={row.id}
                              onClick={(e) => onEventClick(e, row)}
                              sx={{
                                px: 1.75, py: 1,
                                display: "flex", alignItems: "center", gap: 1.25,
                                cursor: "pointer",
                                borderTop: "1px dashed rgba(15,23,42,0.06)",
                                "&:hover": { background: alpha(theme.palette.primary.main, 0.03) },
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: 13, fontWeight: 700, color: "#0f172a",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                  {/* `row.className` is the canonical
                                      compact label already built from
                                      grade + subject tokens by the
                                      backend (see formatClassLabel).
                                      Appending row.gradeName again
                                      produced "3, 2, 4 EQAO Prep -
                                      English · 3, 2, 4" — the exact
                                      duplication users saw in Staff
                                      Portal Week/Day cards. Full
                                      grade-subject mapping is available
                                      via the row's details drawer,
                                      which routes through the shared
                                      GradeSubjectsSummary + modal. */}
                                  {row.className || row.subjectName || "Class"}
                                </Typography>
                                <Typography sx={{
                                  fontSize: 11.5, color: "#64748b", mt: 0.15,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                  {row.staffName || "Unassigned"}
                                  {row.locationName ? (<> · {row.locationName}</>) : null}
                                  {row.classMode ? (<> · {modeLabel(row.classMode)}</>) : null}
                                  {typeof row.studentCount === "number" ? (
                                    <> · {row.studentCount}{typeof row.capacity === "number" && row.capacity > 0 ? `/${row.capacity}` : ""} students</>
                                  ) : null}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={statusMetaFor(row).label}
                                sx={{
                                  height: 20, fontSize: 10.5, fontWeight: 700,
                                  bgcolor: alpha(statusMetaFor(row).color || "#64748b", 0.12),
                                  color: statusMetaFor(row).color || "#64748b",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onEventClick(e, row); }}
                              >
                                <MoreVertIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                )
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

type ViewMode = "week_timetable" | "list" | "agenda" | "day" | "week" | "calendar";
type EnrollmentFilter = "all" | "pending" | "enrolled" | "full";
type RecurringFilter = "all" | "recurring" | "oneoff";
type SortKey = "date" | "time" | "staff" | "class" | "enrollment" | "status";

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  // Staff-role users see a Staff-Portal-scoped student profile; admins keep
  // the canonical /student-history route. Prevents "No data available" when
  // a staff user clicks a student chip inside Class Sessions.
  const buildStudentProfilePath = useStudentProfilePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const { showErrorMessage, showMessage } = useNotifier();
  const { userHasRight, isSystemAdmin, isStaffRole, staffId: currentStaffId } = useUser();
  // Staff Portal mode: non-admin employee with a linked staff record.
  // Drives (a) the filter-options endpoint (self-scoped), (b) the Staff
  // field being locked to self, (c) hiding admin-only Bulk Actions +
  // New Class buttons, (d) the "All My *" filter labels. Admin path is
  // unchanged — an admin ALWAYS sees the academy-wide filter panel.
  const isStaffPortalMode = !isSystemAdmin && (isStaffRole || !!currentStaffId);
  const canEditSchedule = userHasRight(Privileges.ClassScheduleEdit);
  const canEditClasses = userHasRight(Privileges.ClassesEdit);
  const canAttendance = userHasRight(Privileges.AttendanceRecordCreate) || userHasRight(Privileges.AttendanceRecordEdit);
  const isMobileRes = useIsMobile();

  // ── Filters ──
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  // ── Date range ──
  // The Class Schedule list is a FORWARD-LOOKING operations view: it opens on
  // today and never back-fills past sessions the user did not ask for. (The
  // backend applies the same default, so an omitted range can't return history
  // even if a caller skips these params — see ClassScheduleController::index.)
  //
  // An explicit ?from_date/?to_date always wins and is never silently reset
  // forward, so a historical link stays historical across refreshes.
  const [startDate, setStartDate] = useState<Dayjs | null>(
    () => parseDateParam(searchParams.get("from_date")) ?? dayjs().startOf("day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    () => parseDateParam(searchParams.get("to_date"))
      ?? (parseDateParam(searchParams.get("from_date")) ?? dayjs()).endOf("month")
  );
  // staff_id and student_id are URL-driven so a direct link/refresh from a
  // Student Profile's "View All Classes" or the Class Detail page's "View
  // Schedule" preserves the selected staff + student. class_id is also
  // read into the free-text search as a coarse filter until a dedicated
  // per-class filter is wired.
  // Staff is a multi-select (array of IDs). The dropdown has its own search box
  // (see StaffMultiSelect). URL round-trip uses `staff_ids=1,2,3`; the legacy
  // `staff_id=NN` single value is still accepted so old deep-links keep working.
  const [staffIds, setStaffIds] = useState<number[]>(() => {
    const multi = searchParams.get("staff_ids");
    if (multi) return multi.split(",").map(Number).filter((n) => Number.isFinite(n) && n > 0);
    const single = searchParams.get("staff_id");
    return single ? [Number(single)].filter((n) => Number.isFinite(n) && n > 0) : [];
  });
  const [studentFilter, setStudentFilter] = useState(() => searchParams.get("student_id") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  // Subject & Grade are multi-selects (arrays of IDs). Empty = All (no restriction).
  // Both are URL-restorable so a deep-link (grade_ids=1,3&subject_ids=5,7) round-trips.
  const parseIdListParam = (raw: string | null): number[] =>
    raw ? raw.split(",").map((s) => Number(s)).filter((n) => Number.isFinite(n) && n > 0) : [];
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>(() => parseIdListParam(searchParams.get("grade_ids")));
  const [subjectFilter, setSubjectFilter] = useState<number[]>(() => parseIdListParam(searchParams.get("subject_ids")));
  const [modeFilter, setModeFilter] = useState("");
  const [enrollmentFilter, setEnrollmentFilter] = useState<EnrollmentFilter>("all");
  const [recurringFilter, setRecurringFilter] = useState<RecurringFilter>("all");
  // Initialised from the URL so Class Detail → "View Sessions" lands on a
  // list filtered to that exact class (previously the `class_id` query
  // param was dropped and the list fell back to a staff-only filter, which
  // bled in every other class the same teacher owned).
  const [seriesClassId, setSeriesClassId] = useState<string>(
    () => searchParams.get("class_id") || "",
  );
  // Day / Start-time / End-time filters (client-side, over the returned rows).
  // URL-restorable: days=monday,wednesday · start_time=10:00 · end_time=18:00.
  const [selectedDays, setSelectedDays] = useState<string[]>(() => parseDaysParam(searchParams.get("days")));
  const [startTime, setStartTime] = useState<string>(() => hhmm(searchParams.get("start_time")));
  const [endTime, setEndTime] = useState<string>(() => hhmm(searchParams.get("end_time")));
  // Normalized (unique · canonical Mon→Sun order) so equal selections stay stable.
  const normalizedDays = useMemo(() => normalizeDays(selectedDays), [selectedDays]);
  // Range is invalid only when BOTH bounds are set and End is not later than Start.
  const timeRangeInvalid = useMemo(() => !isTimeRangeValid(startTime, endTime), [startTime, endTime]);
  const hasDayTimeFilter = normalizedDays.length > 0 || !!startTime || !!endTime;
  const clearDayTimeFilters = () => { setSelectedDays([]); setStartTime(""); setEndTime(""); };

  // Phase-3: the second filter row (Mode · Status · Enrollment · Type ·
  // Days · Start · End) collapses behind a "More filters" toggle so the
  // filter card no longer eats a full viewport of vertical space. Auto-
  // expands when any of those advanced filters is already active so a
  // deep-link / refresh never hides what's currently filtering.
  const advancedFilterCount = useMemo(() => {
    let n = 0;
    if (modeFilter) n++;
    if (statusFilter) n++;
    if (enrollmentFilter !== "all") n++;
    if (recurringFilter !== "all") n++;
    if (normalizedDays.length > 0) n++;
    if (startTime) n++;
    if (endTime) n++;
    return n;
  }, [modeFilter, statusFilter, enrollmentFilter, recurringFilter, normalizedDays, startTime, endTime]);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState<boolean>(false);
  // Auto-open once when a load/first-render finds active advanced filters
  // (deep-link case). Kept as a one-shot with a ref-like sentinel so a user
  // who intentionally closes the section doesn't get it reopened later.
  const advancedAutoOpenedRef = React.useRef(false);
  useEffect(() => {
    if (advancedAutoOpenedRef.current) return;
    if (advancedFilterCount > 0) {
      setMoreFiltersOpen(true);
      advancedAutoOpenedRef.current = true;
    }
  }, [advancedFilterCount]);

  // ── View / paging / sorting ──
  // Default view is Month (canonical URL value `month`; internally the
  // ViewToggle button still uses `calendar` for backwards compatibility with
  // the FullCalendar plugin name). Parsing is case-insensitive and accepts
  // `month` as an alias for `calendar` so `?view=Month`, `?view=month`,
  // `?view=calendar`, `?view=CALENDAR` all resolve to the month grid. An
  // unknown or missing value silently falls back to Month — matching the
  // spec: "no valid view parameter → activate Month".
  //
  // NOTE: we intentionally do NOT read from localStorage when choosing the
  // initial view. The spec requires the landing view to be Month unless the
  // URL says otherwise — a stale localStorage entry from a previous "List"
  // click was the root cause of the "does not consistently open in Calendar"
  // bug. localStorage is still WRITTEN below for compatibility with any
  // legacy consumer, but never read at initial mount.
  const VALID_VIEWS: ViewMode[] = ["week_timetable", "list", "agenda", "day", "week", "calendar"];
  const parseView = (raw: string | null): ViewMode | null => {
    if (!raw) return null;
    const norm = String(raw).trim().toLowerCase();
    if (norm === "month") return "calendar";
    // Accept a friendly URL alias.
    if (norm === "week-timetable" || norm === "weektimetable") return "week_timetable";
    return (VALID_VIEWS as string[]).includes(norm) ? (norm as ViewMode) : null;
  };
  // Phase-2: default the page to the compact paginated LIST view so users
  // hit the fast slim-payload endpoint immediately. Explicit ?view= URL
  // params and the sticky-localStorage choice still win.
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const fromUrl = parseView(searchParams.get("view"));
    if (fromUrl) return fromUrl;
    try {
      const stored = parseView(localStorage.getItem(VIEW_KEY));
      if (stored) return stored;
    } catch {
      // localStorage inaccessible (private mode / SSR); fall through to default.
    }
    return "list";
  });
  useEffect(() => localStorage.setItem(VIEW_KEY, viewMode), [viewMode]);
  // Keep ?view=<mode> in sync so refresh/deep-link preserves the choice.
  // The internal `calendar` value is written back to the URL as canonical
  // `month` — matches the spec's public URL contract.
  useEffect(() => {
    const canonical = viewMode === "calendar" ? "month" : viewMode;
    const cur = (searchParams.get("view") || "").toLowerCase();
    if (cur === canonical) return;
    const next = new URLSearchParams(searchParams);
    next.set("view", canonical);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Month calendar tracks its own "visible month" so a user-picked multi-month
  // date range (which drives the fetch) doesn't get overwritten by Prev/Next.
  const [visibleMonth, setVisibleMonth] = useState<Dayjs>(() => (startDate || dayjs()).startOf("month"));

  // Day/Week focus date (drives the timeline + fetch range).
  const [focusDate, setFocusDate] = useState<Dayjs>(dayjs());
  const mondayOf = (d: Dayjs) => d.subtract((d.day() + 6) % 7, "day").startOf("day");

  // Day/Week views drive the fetch range from the focused date so real occurrence
  // times/durations are always available for the visible day or week.
  useEffect(() => {
    if (viewMode === "day") {
      setStartDate(focusDate.startOf("day"));
      setEndDate(focusDate.endOf("day"));
    } else if (viewMode === "week" || viewMode === "week_timetable") {
      // Both week-shaped views fetch the same Mon..Sun window so
      // toggling between them never triggers a duplicate API call.
      const mon = mondayOf(focusDate);
      setStartDate(mon);
      setEndDate(mon.add(6, "day").endOf("day"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, focusDate]);

  // Month navigation shifts the FETCH range to the visible month bounds when the
  // user pages with Prev/Today/Next. Does NOT run on filter changes, so a user's
  // custom date range remains intact until they navigate the month explicitly.
  const goToMonth = (m: Dayjs) => {
    const mm = m.startOf("month");
    setVisibleMonth(mm);
    setStartDate(mm.startOf("day"));
    setEndDate(mm.endOf("month"));
  };
  // Month +N-more popover anchoring (per-cell).
  const [morePop, setMorePop] = useState<{ anchor: HTMLElement; rows: any[] } | null>(null);

  // ── Month-calendar container width (drives event density & +N more cap) ──
  // We can't rely on `window.innerWidth` because the sidebar collapse/expand
  // changes the *available* width for the main content without firing a window
  // resize event. A ResizeObserver on the calendar wrapper reacts to that.
  //
  // Bucket the width and only setState when the resulting bucket actually
  // changes (or the width moves by >8px) to avoid ResizeObserver feedback loops
  // and unnecessary re-renders when a scroll bar toggles by 1-2px.
  const monthGridRef = useRef<HTMLDivElement | null>(null);
  const [monthContainerWidth, setMonthContainerWidth] = useState<number>(0);
  useEffect(() => {
    const el = monthGridRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setMonthContainerWidth((prev) => (Math.abs(prev - w) > 8 ? w : prev));
    });
    ro.observe(el);
    // Prime the value synchronously so first paint uses the real width, not 0.
    setMonthContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
    // Re-attach when the calendar view is (re)mounted.
  }, [/* re-run on view remount */]);

  const monthLimits = useMemo(() => getMonthCellLimits(monthContainerWidth), [monthContainerWidth]);
  // Hydrate the paginated page + per_page from the URL so refresh, share
  // and browser Back/Forward keep the user on the same slice. URL is
  // 1-indexed (matches the display + backend contract); internal state
  // stays 0-indexed for the existing MUI Pagination component.
  const [page, setPage] = useState<number>(() => {
    const raw = parseInt(searchParams.get("page") || "1", 10);
    return Number.isFinite(raw) && raw > 0 ? raw - 1 : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const raw = parseInt(searchParams.get("per_page") || "25", 10);
    if (!Number.isFinite(raw) || raw <= 0) return 25;
    return Math.min(100, raw);
  });
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "date", dir: "asc" });

  const [expandedId, setExpandedId] = useState<any>(null);
  const [selected, setSelected] = useState<Set<any>>(new Set());
  // "New Class" opens a student-picker instead of the generic Create Class
  // popup — Class Sessions must funnel through the student-first Assign flow.
  const [selectStudentOpen, setSelectStudentOpen] = useState(false);


  const { data: locationData } = useGetallLocationQuery({ searchText: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });
  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });
  const locationList = useMemo(() => (Array.isArray(locationData?.data) ? (locationData?.data as any[]) : []), [locationData]);
  const subjectList = useMemo(() => (Array.isArray(subjectData?.data) ? (subjectData?.data as any[]) : []), [subjectData]);
  const gradeList = useMemo(() => (Array.isArray(gradeData?.data) ? (gradeData?.data as any[]) : []), [gradeData]);

  // Normalise the multi-select ID sets (unique · sorted · positive ints) so the
  // RTK query key is stable — the same selection never triggers a duplicate fetch.
  const gradeIdsParam = useMemo(
    () => Array.from(new Set(selectedGradeIds.map(Number))).filter((n) => n > 0).sort((a, b) => a - b),
    [selectedGradeIds]
  );
  const subjectIdsParam = useMemo(
    () => Array.from(new Set(subjectFilter.map(Number))).filter((n) => n > 0).sort((a, b) => a - b),
    [subjectFilter]
  );

  // Phase-1 server-side pagination. The existing `page` (0-indexed) and
  // `rowsPerPage` client state are reused to drive the backend page, so
  // the Pagination control at the bottom of the list continues to work
  // and the two systems don't fight over the "current page" concept.
  // Server contract is 1-indexed; convert at the API boundary only.
  // Look up the location id for the current locationFilter name. The
  // Select stores the location NAME (unchanged UI), but the backend
  // filter takes an id, so we translate here.
  const locationIdParam = useMemo(() => {
    if (!locationFilter) return undefined;
    const hit = locationList.find((l: any) => String(l.location_name) === String(locationFilter));
    return hit ? Number(hit.id) : undefined;
  }, [locationFilter, locationList]);

  const listArgs = useMemo(() => ({
    searchText: currentSearchQuery,
    startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
    endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
    // Staff Portal: backend scoping always narrows to Auth::user()->staffId,
    // but a URL like ?staff_ids=... arriving from a shared admin link would
    // produce "no results" rather than "my classes". In Staff Portal we drop
    // the filter entirely so the backend's Tutor scope alone applies.
    staff_ids: isStaffPortalMode ? [] : staffIds,
    studentId: studentFilter,
    status: statusFilter,
    // Multi-subject / multi-grade filters → arrays of IDs. Match Any is applied
    // server-side against the real grade/subject pivot; when both are set the
    // backend matches valid grade-subject PAIRS (not two flattened filters).
    subject_ids: subjectIdsParam,
    grade_ids: gradeIdsParam,
    // Phase-2: former client-side filters now pushed to the backend so
    // pagination is authoritative for the full filter set. The client-side
    // `filtered` memo still runs as a no-op safety net for older backend
    // deploys / concurrent tabs; the server has already applied these.
    location_id: locationIdParam,
    mode: modeFilter || undefined,
    enrollment: enrollmentFilter !== "all" ? enrollmentFilter : undefined,
    recurring: recurringFilter !== "all" ? recurringFilter : undefined,
    days: normalizedDays.length > 0 ? normalizedDays : undefined,
    time_from: startTime || undefined,
    time_to: endTime || undefined,
    // Single-class scope. Set only when Class Detail links in via
    // ?class_id=NNN so the server narrows directly to that assign_classes id
    // (the client-side `seriesClassId` filter still runs as a belt-and-braces
    // defence for stale server builds).
    class_id: seriesClassId || undefined,
    page: page + 1,
    per_page: rowsPerPage,
  }), [currentSearchQuery, startDate, endDate, staffIds, studentFilter, statusFilter, subjectIdsParam, gradeIdsParam, locationIdParam, modeFilter, enrollmentFilter, recurringFilter, normalizedDays, startTime, endTime, seriesClassId, page, rowsPerPage]);

  const { data, isLoading, isFetching, isError, refetch } = useGetClassScheduleQuery(listArgs);

  // NB: page reset on filter change is handled by the existing effect at
  // ~line 892 (deps include every server + client filter), so no dedicated
  // reset is needed here for the paginated query.

  // KPI cards — cheap aggregate endpoint scoped to the same server filters
  // as the list so the header always agrees with the visible rows.
  // Independent from the paginated list request, so paging does not
  // refetch KPIs.
  const summaryArgs = useMemo(() => ({
    searchText: currentSearchQuery,
    startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
    endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
    // See listArgs — Staff Portal drops the staff filter; admin sends the
    // selected ids as a multi-value array.
    staff_ids: isStaffPortalMode ? [] : staffIds,
    studentId: studentFilter,
    status: statusFilter,
    subject_ids: subjectIdsParam,
    grade_ids: gradeIdsParam,
    location_id: locationIdParam,
    mode: modeFilter || undefined,
    enrollment: enrollmentFilter !== "all" ? enrollmentFilter : undefined,
    recurring: recurringFilter !== "all" ? recurringFilter : undefined,
    days: normalizedDays.length > 0 ? normalizedDays : undefined,
    time_from: startTime || undefined,
    time_to: endTime || undefined,
  }), [currentSearchQuery, startDate, endDate, staffIds, studentFilter, statusFilter, subjectIdsParam, gradeIdsParam, locationIdParam, modeFilter, enrollmentFilter, recurringFilter, normalizedDays, startTime, endTime]);
  const { data: summaryData } = useGetClassScheduleSummaryQuery(summaryArgs);

  const { data: staffData } = useGetTeamQuery({ searchText: "", id: "" });
  const staffList = useMemo(() => (Array.isArray(staffData?.data) ? (staffData?.data as any[]) : []), [staffData]);

  // Student/Staff options are scoped to the current date window — only students
  // ASSIGNED to a visible schedule in range appear (assigned-students-only).
  // Staff Portal mode uses a dedicated self-scoped endpoint that returns ONLY
  // the logged-in staff's teaching world (own students / locations / grades /
  // subjects). Admin endpoint is unchanged.
  const { data: adminFilterOptionsData, isFetching: adminOptionsLoading } = useGetClassScheduleFilterOptionsQuery(
    isStaffPortalMode ? (undefined as any) : {
      startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
    },
    { skip: isStaffPortalMode }
  );
  const { data: staffFilterOptionsData, isFetching: staffOptionsLoading } = useGetStaffPortalClassSessionsFilterOptionsQuery(
    undefined,
    { skip: !isStaffPortalMode }
  );
  const filterOptionsData = isStaffPortalMode ? staffFilterOptionsData : adminFilterOptionsData;
  const optionsLoading = isStaffPortalMode ? staffOptionsLoading : adminOptionsLoading;
  const staffOptions = useMemo(() => normalizeStaffOptions((filterOptionsData as any)?.data), [filterOptionsData]);
  const studentOptions = useMemo(() => normalizeStudentOptions((filterOptionsData as any)?.data), [filterOptionsData]);
  // Grades and subjects (with their grade_ids[] mapping) come from the SAME
  // filter-options response. We fall back to the standalone /grades and
  // /subjects lookups if the endpoint hasn't been redeployed yet — the mapping
  // just won't be present and Subjects will behave as "All Subjects".
  const gradeOptionsFO = useMemo(() => normalizeGradeOptions((filterOptionsData as any)?.data), [filterOptionsData]);
  const subjectOptionsFO = useMemo(() => normalizeSubjectOptions((filterOptionsData as any)?.data), [filterOptionsData]);
  const gradeOptions = useMemo(() => {
    if (gradeOptionsFO.length > 0) return gradeOptionsFO;
    return gradeList.map((g: any) => ({ id: Number(g.id), name: String(g.grade || "") }));
  }, [gradeOptionsFO, gradeList]);
  const allSubjectOptions = useMemo(() => {
    if (subjectOptionsFO.length > 0) return subjectOptionsFO;
    return subjectList.map((s: any) => ({ id: Number(s.id), name: String(s.subject_name || ""), grade_ids: [] as number[] }));
  }, [subjectOptionsFO, subjectList]);
  // Subject options are dependent on the selected Grade(s). With no grade
  // selected the full active-subject list is shown (spec §2). With one or more
  // grades selected the dropdown shows the UNION of subjects mapped to any of
  // them (Match Any), de-duplicated by id.
  const scopedSubjectOptions = useMemo(
    () => subjectsForGrades(allSubjectOptions, selectedGradeIds),
    [allSubjectOptions, selectedGradeIds]
  );
  const scopedSubjectIdSet = useMemo(() => new Set(scopedSubjectOptions.map((s) => s.id)), [scopedSubjectOptions]);

  // Spec §5 · When the Grade selection changes, drop any selected Subject that
  // is no longer valid under the new grade scope. Preserves valid selections so
  // the URL / chip / query stay consistent. Only runs after the FO data has
  // loaded — otherwise a first paint with no mapping would wipe a URL-restored
  // subject filter.
  useEffect(() => {
    if (allSubjectOptions.length === 0) return;         // mapping not yet loaded
    if (selectedGradeIds.length === 0) return;          // "All Grades" → nothing to prune
    if (subjectFilter.length === 0) return;
    const next = subjectFilter.filter((id) => scopedSubjectIdSet.has(Number(id)));
    if (next.length !== subjectFilter.length) setSubjectFilter(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGradeIds, allSubjectOptions]);
  // Map of staff id → display name for chips/renderValue. Rebuilt whenever the
  // staff options list changes (dropdown reload or portal-scope swap).
  const staffNameById = useMemo(() => {
    const m = new Map<number, string>();
    staffOptions.forEach((o) => m.set(Number(o.value), o.label));
    return m;
  }, [staffOptions]);
  const selectedStudentOption = useMemo(() => studentOptions.find((o) => String(o.value) === String(studentFilter)) || null, [studentOptions, studentFilter]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const schedules: any[] = useMemo(() => (Array.isArray(data?.data) ? (data?.data as any[]) : []), [data]);
  const summary = useMemo(() => (data as any)?.summary ?? null, [data]);
  // Phase-1: KPIs moved to a dedicated aggregate endpoint so paging does
  // not force recomputing them. Fall back to any KPIs the list response
  // still ships (older backend deploys) so the cards never blank out.
  const kpis = useMemo(() => {
    const fromSummary = (summaryData as any)?.data?.kpis;
    if (fromSummary && typeof fromSummary === "object") return fromSummary;
    return (summary?.kpis as Record<string, number> | undefined) ?? {};
  }, [summaryData, summary]);
  // Pagination metadata from the backend. Defaults keep the UI correct
  // during the very first fetch and for any legacy response missing it.
  const pagination = useMemo(() => {
    const p = (data as any)?.pagination;
    return {
      current_page: p?.current_page ?? page,
      per_page: p?.per_page ?? rowsPerPage,
      total: p?.total ?? schedules.length,
      last_page: p?.last_page ?? 1,
      from: p?.from ?? (schedules.length ? 1 : 0),
      to: p?.to ?? schedules.length,
      has_next_page: !!p?.has_next_page,
      has_previous_page: !!p?.has_previous_page,
    };
  }, [data, page, rowsPerPage, schedules.length]);

  // ── Client-side dimensions (server already applied date/staff/student/status) ──
  const filtered = useMemo(() => {
    let rows = schedules;
    if (seriesClassId) rows = rows.filter((r) => String(r.classId) === String(seriesClassId));
    if (locationFilter) rows = rows.filter((r) => String(r.locationName || "") === locationFilter);
    // Subject & Grade are filtered server-side (Match Any over the real class
    // grade/subject pivot, with valid-pair matching when both are set), so there
    // is no client-side grade/subject pass here.
    if (modeFilter) rows = rows.filter((r) => String(r.classMode || "") === modeFilter);
    if (enrollmentFilter !== "all") {
      rows = rows.filter((r) => {
        const c = r.studentCount ?? 0;
        const cap = r.capacity ?? null;
        if (enrollmentFilter === "pending") return c === 0;
        if (enrollmentFilter === "enrolled") return c > 0;
        if (enrollmentFilter === "full") return cap != null && c >= cap;
        return true;
      });
    }
    if (recurringFilter !== "all") {
      rows = rows.filter((r) => (recurringFilter === "recurring" ? r.isRecurring : !r.isRecurring));
    }
    // Day / Start / End — client-side over the row's ACTUAL occurrence date and
    // normalized "HH:mm" DB times (Match Any days; start >= / end <= window).
    // An invalid time range is ignored here (the End Time field shows the error).
    if (normalizedDays.length > 0 || startTime || endTime) {
      rows = rows.filter((r) => rowMatchesDayTime(r, { days: normalizedDays, start: startTime, end: endTime }));
    }
    return rows;
  }, [schedules, seriesClassId, locationFilter, modeFilter, enrollmentFilter, recurringFilter, normalizedDays, startTime, endTime]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (r: any) => {
      switch (sort.key) {
        case "date": return `${scheduleDateKey(r)} ${r.startTime || ""}`;
        case "time": return r.startTime || "";
        case "staff": return (r.staffName || "").toLowerCase();
        case "class": return (r.className || "").toLowerCase();
        case "enrollment": return r.studentCount ?? 0;
        case "status": return statusMetaFor(r).label;
        default: return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sort]);

  // Server pagination returns just the current page's rows already; the
  // client-side `filtered`+`sorted` pipeline still runs over them for
  // client-only filters (location/mode/enrollment/day/time) and the
  // column-header sort, but no further slicing is needed.
  const paged = useMemo(() => sorted, [sorted]);

  // Prune bulk selections that are no longer visible/valid after a filter change
  // (e.g. a subject deselection). Keeps the "N selected" count and bulk actions
  // scoped to the currently filtered rows only.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(sorted.map((r) => r.id));
      const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sorted]);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, staffIds, studentFilter, statusFilter, currentSearchQuery, locationFilter, subjectFilter, selectedGradeIds, modeFilter, enrollmentFilter, recurringFilter, seriesClassId, normalizedDays, startTime, endTime]);

  // Keep ?student_id in sync with the Student filter (deep-link + refresh safe),
  // preserving every other query param. Reset clears it via setStudentFilter("").
  useEffect(() => {
    const cur = searchParams.get("student_id") || "";
    if (cur === (studentFilter || "")) return;
    const next = new URLSearchParams(searchParams);
    if (studentFilter) next.set("student_id", studentFilter);
    else next.delete("student_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentFilter]);

  // Keep ?days / ?start_time / ?end_time in sync (deep-link + refresh safe),
  // preserving every other query param. Only writes when something changed, so
  // it never fights the student_id sync above or loops.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const daysStr = serializeDays(normalizedDays);
    if (daysStr) next.set("days", daysStr); else next.delete("days");
    if (startTime) next.set("start_time", startTime); else next.delete("start_time");
    if (endTime) next.set("end_time", endTime); else next.delete("end_time");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedDays, startTime, endTime]);

  // Keep ?grade_ids / ?subject_ids in sync so a deep-link restores both filters
  // (and the scoped Subject list) on refresh. Uses the normalised (sorted,
  // unique, positive) id sets so the URL never churns for the same selection.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (gradeIdsParam.length > 0) next.set("grade_ids", gradeIdsParam.join(","));
    else next.delete("grade_ids");
    if (subjectIdsParam.length > 0) next.set("subject_ids", subjectIdsParam.join(","));
    else next.delete("subject_ids");
    // Staff multi-select — write `staff_ids`, retire the legacy `staff_id`
    // key so a URL never carries both at once.
    if (staffIds.length > 0) next.set("staff_ids", staffIds.join(","));
    else next.delete("staff_ids");
    next.delete("staff_id");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeIdsParam, subjectIdsParam, staffIds]);

  // Keep ?from_date / ?to_date in sync so a refresh preserves the user's chosen
  // range — including a deliberately historical one, which must never snap back
  // to today. Same write-only-on-change guard as the syncs above.
  //
  // Day/Week are excluded: those views derive the range from focusDate and
  // recompute it on mount, so writing it here would churn the URL on every
  // arrow-key navigation without changing what loads.
  useEffect(() => {
    if (viewMode === "day" || viewMode === "week" || viewMode === "week_timetable") return;
    const next = new URLSearchParams(searchParams);
    if (startDate) next.set("from_date", startDate.format("YYYY-MM-DD")); else next.delete("from_date");
    if (endDate) next.set("to_date", endDate.format("YYYY-MM-DD")); else next.delete("to_date");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, viewMode]);

  // Phase-2: keep ?page and ?per_page in sync so a refresh, share or a
  // Back/Forward navigation lands on the same paginated slice. Only in the
  // compact list view — the other views don't paginate visibly.
  useEffect(() => {
    if (viewMode !== "list") return;
    const next = new URLSearchParams(searchParams);
    // page is 0-indexed internally; URL uses 1-indexed to match the visible
    // count and the backend contract.
    if (page > 0) next.set("page", String(page + 1)); else next.delete("page");
    if (rowsPerPage !== 25) next.set("per_page", String(rowsPerPage)); else next.delete("per_page");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, viewMode]);

  // A student_id that is invalid/unauthorized or has no assigned schedules in the
  // current scope is never in the (assigned-only) options → drop it safely so the
  // dropdown, chip and results stay consistent ("All Assigned Students").
  useEffect(() => {
    if (optionsLoading || !studentFilter) return;
    if (studentOptions.length === 0) { setStudentFilter(""); return; }
    if (!studentOptions.some((o) => String(o.value) === String(studentFilter))) setStudentFilter("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentOptions, optionsLoading, studentFilter]);

  // Close the action menu whenever the visible result set can change under it —
  // filters, view, pagination, sort, or date range. (Outside-click and Escape are
  // handled natively by the MUI Menu.)
  useEffect(() => {
    setMenuPos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, staffIds, studentFilter, statusFilter, currentSearchQuery, locationFilter, subjectFilter, selectedGradeIds, modeFilter, enrollmentFilter, recurringFilter, seriesClassId, normalizedDays, startTime, endTime, viewMode, page, rowsPerPage, sort]);

  // Never act on a stale row: if the selected row leaves the current result set
  // (after re-fetch/filter/sort), drop the menu selection AND prune bulk-selected
  // rows that are no longer visible (so bulk actions only ever touch filtered rows).
  useEffect(() => {
    if (actionRow && !sorted.some((r) => r.id === actionRow.id)) {
      setMenuPos(null);
      setActionRow(null);
    }
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(sorted.map((r) => r.id));
      let changed = false;
      const next = new Set<any>();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  // ── Mutations ──
  const [releaseClass, { isLoading: releaseLoading }] = useReleaseClassMutation();

  // ── Row action menu + dialogs ──
  // Position-based anchoring (captured from the clicked button's rect) so the
  // menu opens beside the exact row and can never fall back to the page's
  // top-left — even when the row subtree re-renders and detaches a DOM anchor.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [actionRow, setActionRow] = useState<any>(null);

  // View Details drawer (occurrence-level).
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState<any>(null);

  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  // Switch Staff owns its own candidate/availability/reason state — see
  // SwitchStaffDialog. The page only tracks whether it is open.

  // Duplicate / Assign / Bulk-switch dialogs
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [bulkSwitchOpen, setBulkSwitchOpen] = useState(false);
  // Remove Student / Schedule Makeup (student-specific occurrence workflow)
  const [makeupOpen, setMakeupOpen] = useState(false);
  const [makeupMode, setMakeupMode] = useState<"remove" | "makeup">("remove");

  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseType, setReleaseType] = useState<"remove" | "remove_future" | "makeup">("remove");
  const [releaseReason, setReleaseReason] = useState("");       // dropdown selection
  const [releaseReasonOther, setReleaseReasonOther] = useState(""); // required when "Other"
  const [releaseNotes, setReleaseNotes] = useState("");            // optional
  const [releaseTried, setReleaseTried] = useState(false);
  const [makeupDate, setMakeupDate] = useState("");
  const [makeupStartTime, setMakeupStartTime] = useState("");
  const [makeupEndTime, setMakeupEndTime] = useState("");
  const [makeupStaffId, setMakeupStaffId] = useState("");

  // Canonical class-level cancellation reasons. "Other" opens a required text box;
  // the real typed reason is stored (never the bare word "Other").
  const RELEASE_REASONS = [
    "Staff unavailable", "Staff illness", "Weather", "Location unavailable",
    "Student request", "Scheduling conflict", "Holiday or closure", "Administrative decision", "Other",
  ];
  const effectiveReleaseReason = releaseReason === "Other" ? releaseReasonOther.trim() : releaseReason;
  const releaseReasonValid = releaseReason !== "" && (releaseReason !== "Other" || releaseReasonOther.trim().length > 0);

  const actionKeys = (row: any) =>
    row?.id != null
      ? { occurrenceId: row.id }
      : { scheduleId: row?.scheduleId };

  // Capture the clicked button's viewport rect and open the menu just below its
  // right edge. Position-based (not DOM-node-based) so a re-render can't detach
  // the anchor; MUI keeps it inside the viewport (shifts up near the bottom).
  const handleOpenActionMenu = (e: React.MouseEvent<HTMLElement>, row: any) => {
    e.stopPropagation?.();
    const el = e.currentTarget as HTMLElement;
    const rect = el?.getBoundingClientRect?.();
    if (rect && rect.width) {
      setMenuPos({ top: rect.bottom + 4, left: rect.right });
    } else {
      setMenuPos({ top: (e as any).clientY ?? 120, left: (e as any).clientX ?? 120 });
    }
    setActionRow(row);
  };
  const handleCloseActionMenu = () => setMenuPos(null);

  const openDetailsDrawer = (row: any) => {
    setDetailsRow(row);
    setDetailsOpen(true);
  };

  // ── Per-row action gating (status / recurring / students / permission) ──
  //
  // Staff ownership rule (2026-08-23): a Staff Portal teacher whose
  // authenticated `staffId` matches the occurrence's `staff_id` (or the
  // per-occurrence override, when the row also carries a legacy
  // `assigned_staff_id`) is treated as the OWNER of that class. Owner
  // status unlocks the same staff-safe actions the shared
  // `attendance.access` middleware unlocks on the backend — Edit
  // Schedule, Attendance, Turn Out, Schedule Makeup, Release —
  // regardless of whether the user holds the global `ClassSchedule_Edit`
  // admin permission. Administrative-only actions (Switch Staff,
  // Assign Student — enrollment authority) stay on the admin gate.
  const ownsOccurrence = (row: any): boolean => {
    if (!isStaffPortalMode || currentStaffId == null) return false;
    const rowStaffId = row?.staffId ?? row?.staff_id ?? row?.staff?.id;
    return rowStaffId != null && Number(rowStaffId) === Number(currentStaffId);
  };

  const rowActionState = (row: any) => {
    const cancelled = row?.status === "cancelled" || row?.is_cancelled;
    const completed = row?.status === "completed";
    const hasStudents = (row?.studentCount ?? 0) > 0;
    const full = row?.capacity != null && (row?.studentCount ?? 0) >= row.capacity;
    const hasSchedule = !!row?.scheduleId;
    const isOwner = ownsOccurrence(row);
    // Own-class access satisfies the "edit" gate for staff-safe actions.
    const canEditThisRow = canEditSchedule || isOwner;
    const canAttendThisRow = canAttendance || isOwner;
    // Enrollment authority (Assign Student, Switch Staff) is NOT
    // granted by ownership — those remain admin-only.
    const noEditPerm = "You don't have edit permission";
    const notOwnClass = "You can only edit your own classes.";
    const missingEditWhy = isStaffPortalMode ? notOwnClass : noEditPerm;
    const needStudent = "Assign a student to this session first";
    return {
      edit: { on: canEditThisRow && hasSchedule && !cancelled, why: !canEditThisRow ? missingEditWhy : !hasSchedule ? needStudent : cancelled ? "A cancelled session can't be edited" : "" },
      attendance: { on: canAttendThisRow && hasStudents && !cancelled, why: !canAttendThisRow ? (isStaffPortalMode ? notOwnClass : "You don't have attendance permission") : !hasStudents ? "No students to record attendance for" : cancelled ? "Session is cancelled" : "" },
      assignStudent: { on: canEditSchedule && !!row?.classId && !cancelled && !full, why: !canEditSchedule ? noEditPerm : cancelled ? "Session is cancelled" : full ? "Class is at full capacity" : "" },
      switchStaff: { on: canEditSchedule && !cancelled && !completed, why: !canEditSchedule ? noEditPerm : cancelled ? "Session is cancelled" : completed ? "Session is completed" : "" },
      release: { on: canEditThisRow && !cancelled, why: !canEditThisRow ? missingEditWhy : cancelled ? "Already cancelled" : "" },
      // Student-specific occurrence action (never cancels the class for everyone).
      // "Turn Out" is the user-facing label; the underlying operation is still the
      // per-student occurrence removal (removeStudentFromOccurrence).
      turnOut: { on: canEditThisRow && hasStudents && !cancelled, why: !canEditThisRow ? missingEditWhy : !hasStudents ? "No students are assigned to this occurrence." : cancelled ? "This occurrence has already been released." : "" },
      scheduleMakeup: { on: canEditThisRow && hasStudents && !cancelled, why: !canEditThisRow ? missingEditWhy : !hasStudents ? "No students assigned" : cancelled ? "Session is cancelled" : "" },
    };
  };

  // Drawer/expanded "quick action" dispatcher → opens the right dialog for the row.
  const dispatchRowAction = (row: any, type: string) => {
    setActionRow(row);
    switch (type) {
      // Edit Schedule opens the OCCURRENCE editor. `row.id` is
      // `class_occurrences.id`; `row.scheduleId` is the legacy
      // `class_schedules.id` and must NOT be used here — the destination
      // page queries class_occurrences and returns "Class session not
      // found" for a legacy id. Skip the legacy redirect entirely and
      // navigate straight to the occurrence-edit route.
      case "edit": if (row?.id) navigate(`/class-sessions/${row.id}/edit`); break;
      // View Full Details → canonical class-details page. MUST use the
      // recurring class id (assign_classes.id, called `classId` on the
      // row), NOT the schedule pattern id (assign_classes_schedules.id)
      // or the occurrence id (class_occurrences.id). Those live in
      // different id spaces and hitting /class-schedules/{scheduleId}
      // produces the "Class not found" screen — ClassScheduleDetails
      // resolves through useGetClassScheduleDetailsQuery → assign_classes.
      // This was the source of the "/class-schedules/805 → Class not
      // found" screenshot: 805 was the schedule pattern id, and
      // assign_classes had no matching row (id space runs up to ~30).
      case "viewFullDetails": {
        const classId = row?.classId;
        if (classId) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          navigate(`/class-schedules/${classId}?return_url=${returnUrl}`);
        }
        break;
      }
      case "switchStaff": handleOpenSwitchDialog(); break;
      case "assignStudent": setAssignOpen(true); break;
      // Real attendance screen, scoped to this session's date — never the class edit page.
      case "attendance":
        if (row?.id) {
          navigate(
            `/class-sessions/${row.id}/attendance?return_url=${encodeURIComponent("/class-schedule/list")}`
          );
        } else {
          navigate(`/attendance-record/list`);
        }
        break;
      case "release": handleOpenReleaseDialog("remove"); break;
      // Cancel/Delete are consolidated into Release Class (no separate workflows).
      // View Series and Duplicate were removed from the occurrence action set.
      case "turnOut": setMakeupMode("remove"); setMakeupOpen(true); break;
      case "scheduleMakeup": setMakeupMode("makeup"); setMakeupOpen(true); break;
      case "viewStudent": break; // handled where student id is known
      default: break;
    }
  };

  const handleOpenSwitchDialog = () => {
    handleCloseActionMenu();
    setSwitchDialogOpen(true);
  };

  /**
   * Post-mutation cleanup (§10). The DATA refresh is handled by RTK Query tag
   * invalidation — every dependent view (list, summary, filter options, details
   * drawer, All Classes, Student Profile Calendar, Today's/Weekly Schedule,
   * attendance) subscribes to the `classSchedule` tag, so they all refetch
   * without a browser refresh.
   *
   * What tags can't do is dismiss UI that is still pointing at a row which may
   * have just left the list, so that is done explicitly here.
   */
  const refreshAfterMutation = () => {
    setMenuPos(null);
    setActionRow(null);
    setDetailsOpen(false);
    setDetailsRow(null);
    setExpandedId(null);
    clearSelection();
  };

  const handleOpenReleaseDialog = (presetType: "remove" | "remove_future" | "makeup" = "remove") => {
    handleCloseActionMenu();
    setReleaseType(presetType);
    setReleaseReason(""); setReleaseReasonOther(""); setReleaseNotes(""); setReleaseTried(false);
    setMakeupDate("");
    // Default the slot to the original session's time (normalised to HH:mm).
    setMakeupStartTime(String(actionRow?.startTime || "").slice(0, 5));
    setMakeupEndTime(String(actionRow?.endTime || "").slice(0, 5));
    // Never pre-select a replacement teacher — availability decides it.
    setMakeupStaffId("");
    setReleaseDialogOpen(true);
  };

  const submitRelease = async () => {
    const payload: any = { ...actionKeys(actionRow), releaseType, reason: effectiveReleaseReason, notes: releaseNotes };
    if (releaseType === "makeup") {
      payload.makeupDate = makeupDate;
      payload.makeupStartTime = makeupStartTime;
      payload.makeupEndTime = makeupEndTime;
      payload.makeupStaffId = makeupStaffId ? Number(makeupStaffId) : undefined;
    }
    const res = await releaseClass(payload).unwrap();
    if (res.status) {
      showMessage(res.message);
      setReleaseDialogOpen(false);
      setActionRow(null);
    } else showErrorMessage(res.message || "Failed to release class");
  };

  const handleReleaseClass = async () => {
    if (!actionRow) return;
    // Reason is mandatory for every class-level release/cancellation.
    if (!releaseReasonValid) { setReleaseTried(true); return; }
    try {
      if (releaseType === "remove_future") {
        const ok = await confirm({
          title: "Remove this and all future classes?",
          message: "This cancels the selected session and every future session in the same recurring series. Past, completed, invoiced, cancelled, makeup and manually overridden sessions are not affected. This cannot be undone.",
          variant: "danger",
          confirmText: "Remove all future",
        });
        if (!ok) return;
      }
      await submitRelease();
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to release class");
    }
  };

  // ── Bulk selection ──
  const pageIds = paged.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const somePageSelected = pageIds.some((id) => selected.has(id));
  const toggleSelectAllPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleSelectRow = (id: any) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearSelection = () => setSelected(new Set());
  const selectedRows = useMemo(() => sorted.filter((r) => selected.has(r.id)), [sorted, selected]);

  const exportRows = (rows: any[]) => {
    if (!rows.length) {
      showErrorMessage("Nothing to export");
      return;
    }
    const headers = ["Date", "Start", "End", "Class", "Staff", "Enrolled", "Capacity", "Location", "Mode", "Type", "Status"];
    const esc = (v: any) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map((r) =>
      [scheduleDateKey(r), r.startTime || "", r.endTime || "", r.className || "", r.staffName || "", r.studentCount ?? 0, r.capacity ?? "", r.locationName || "", r.classMode || "", r.isRecurring ? "Recurring" : "One-off", statusMetaFor(r).label].map(esc).join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `class-schedules_${dayjs().format("YYYYMMDD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showMessage(`Exported ${rows.length} class occurrence(s)`);
  };

  const handleBulkCancel = async () => {
    const ok = await confirm({
      title: `Cancel ${selectedRows.length} session(s)?`,
      message: "Each selected session will be cancelled. Other sessions are unaffected. This cannot be undone.",
      variant: "danger",
      confirmText: "Cancel selected",
    });
    if (!ok) return;
    let done = 0;
    for (const r of selectedRows) {
      try {
        const res = await releaseClass({ ...actionKeys(r), releaseType: "remove", reason: "" }).unwrap();
        if (res.status) done++;
      } catch {
        /* continue */
      }
    }
    showMessage(`Cancelled ${done} of ${selectedRows.length} session(s)`);
    clearSelection();
  };

  // ── KPI cards ──
  const applyKpiFilter = (key: string) => {
    const today = dayjs();
    switch (key) {
      case "todaysClasses":
        setStartDate(today.startOf("day")); setEndDate(today.endOf("day")); setStatusFilter(""); break;
      case "thisWeek":
        setStartDate(today.startOf("week")); setEndDate(today.endOf("week")); setStatusFilter(""); break;
      case "enrollmentPending":
        setEnrollmentFilter("pending"); break;
      case "completedToday":
        setStartDate(today.startOf("day")); setEndDate(today.endOf("day")); setStatusFilter("completed"); break;
      // "cancelled" is deliberately absent — that card is display-only. Cancelled
      // sessions are excluded from this list server-side, so filtering to them
      // here would only ever produce an empty table.
      case "recurringSeries":
        setRecurringFilter("recurring"); break;
    }
  };

  const kpiCards = [
    { key: "todaysClasses", label: "Today's Classes", icon: <TodayOutlinedIcon />, color: "#3b82f6" },
    { key: "thisWeek", label: "This Week", icon: <DateRangeOutlinedIcon />, color: "#6366f1" },
    { key: "enrollmentPending", label: "Enrollment Pending", icon: <HourglassEmptyOutlinedIcon />, color: "#f59e0b" },
    { key: "completedToday", label: "Completed Today", icon: <CheckCircleOutlineIcon />, color: "#22c55e" },
    // Display-only: cancelled sessions are excluded from the list, so this card
    // reports a statistic (counted server-side over the same filters) rather
    // than acting as a filter that could only return an empty table.
    { key: "cancelled", label: "Cancelled", icon: <CancelOutlinedIcon />, color: "#ef4444", readOnly: true },
    { key: "recurringSeries", label: "Recurring Series", icon: <EventRepeatIcon />, color: RECURRING_ACCENT },
  ];

  // ── Active filter chips ──
  const activeChips: Array<{ label: string; onDelete: () => void }> = [];
  // One removable chip per selected staff member (mirrors subject / grade).
  staffIds.forEach((sid) => {
    const name = staffNameById.get(Number(sid)) ?? `#${sid}`;
    activeChips.push({ label: `Staff: ${name}`, onDelete: () => setStaffIds((prev) => prev.filter((x) => x !== sid)) });
  });
  if (studentFilter && selectedStudentOption) activeChips.push({ label: `Student: ${selectedStudentOption.label}`, onDelete: () => setStudentFilter("") });
  if (statusFilter) activeChips.push({ label: `Status: ${statusFilter}`, onDelete: () => setStatusFilter("") });
  if (locationFilter) activeChips.push({ label: `Location: ${locationFilter}`, onDelete: () => setLocationFilter("") });
  // One removable chip per selected subject (each independently clearable).
  subjectFilter.forEach((sid) => {
    const name = subjectList.find((s: any) => Number(s.id) === Number(sid))?.subject_name ?? `#${sid}`;
    activeChips.push({ label: `Subject: ${name}`, onDelete: () => setSubjectFilter((prev) => prev.filter((x) => x !== sid)) });
  });
  // One removable chip per selected grade (each independently clearable).
  selectedGradeIds.forEach((gid) => {
    const name = gradeList.find((g: any) => Number(g.id) === Number(gid))?.grade ?? `#${gid}`;
    activeChips.push({ label: `Grade: ${name}`, onDelete: () => setSelectedGradeIds((prev) => prev.filter((x) => x !== gid)) });
  });
  if (modeFilter) activeChips.push({ label: `Mode: ${modeLabel(modeFilter)}`, onDelete: () => setModeFilter("") });
  if (enrollmentFilter !== "all") activeChips.push({ label: `Enrollment: ${enrollmentFilter}`, onDelete: () => setEnrollmentFilter("all") });
  if (recurringFilter !== "all") activeChips.push({ label: recurringFilter === "recurring" ? "Recurring" : "One-off", onDelete: () => setRecurringFilter("all") });
  if (seriesClassId) activeChips.push({ label: "Single series", onDelete: () => setSeriesClassId("") });
  // Days / Start / End — one removable chip per group (Days clears all days at once).
  if (normalizedDays.length > 0) activeChips.push({ label: `Days: ${normalizedDays.map(dayLabel).join(", ")}`, onDelete: () => setSelectedDays([]) });
  if (startTime) activeChips.push({ label: `Start Time: From ${fmt12(startTime)}`, onDelete: () => setStartTime("") });
  if (endTime) activeChips.push({ label: `End Time: Until ${fmt12(endTime)}`, onDelete: () => setEndTime("") });

  const resetFilters = () => {
    setSearchQuery(""); setCurrentSearchQuery("");
    // Reset returns to the DEFAULT window (today → end of month), not to the
    // start of the month — otherwise Reset would re-introduce the past dates the
    // list is meant to keep out.
    setStartDate(dayjs().startOf("day")); setEndDate(dayjs().endOf("month"));
    setStaffIds([]); setStudentFilter(""); setStatusFilter("");
    setLocationFilter(""); setSubjectFilter([]); setSelectedGradeIds([]); setModeFilter("");
    setEnrollmentFilter("all"); setRecurringFilter("all"); setSeriesClassId("");
    setSelectedDays([]); setStartTime(""); setEndTime("");
  };

  const seriesTooltip = (row: any) => {
    if (!row.isRecurring) return "";
    const days = (row.seriesDays || []).length ? (row.seriesDays as string[]).join(", ") : "Recurring";
    const range = row.seriesStart && row.seriesEnd ? `${dayjs(row.seriesStart).format("MMM D")} → ${dayjs(row.seriesEnd).format("MMM D, YYYY")}` : "";
    const count = row.seriesCount ? `${row.seriesCount} sessions` : "";
    return [days, range, count].filter(Boolean).join(" · ");
  };

  // ── Sortable header cell ──
  const SortHead: React.FC<{ label: string; sortKey?: SortKey; width?: any; align?: "left" | "right" | "center" }> = ({ label, sortKey, width, align = "left" }) => (
    <Box
      onClick={sortKey ? () => setSort((p) => ({ key: sortKey, dir: p.key === sortKey && p.dir === "asc" ? "desc" : "asc" })) : undefined}
      sx={{
        px: 2, py: 1.25, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
        color: "text.secondary", cursor: sortKey ? "pointer" : "default", userSelect: "none",
        display: "flex", alignItems: "center", gap: 0.5, width, justifyContent: align === "right" ? "flex-end" : "flex-start",
        "&:hover": sortKey ? { color: "text.primary" } : undefined,
      }}
    >
      {label}
      {sortKey && sort.key === sortKey && (sort.dir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardIcon sx={{ fontSize: 13 }} />)}
    </Box>
  );

  // ── Students cell — count + bar + clickable roster ──
  // Renders the enrollment count, a mini progress bar, and the actual
  // enrolled students below the bar so admins don't have to expand the row
  // to see who's in the class. Students come from the backend list payload
  // (`row.students`: `[{id, name}]`), so no extra fetch is needed.
  //
  // Layout follows the spec: up to 3 names visible by default, "+N more"
  // button to reveal the rest inline (does NOT expand the outer row).
  // Every name is a StudentProfileLink → /student-history/{id} with
  // stopPropagation baked in, so clicking a name never toggles the row's
  // checkbox / drawer / expander.
  const StudentsCell: React.FC<{ row: any }> = ({ row }) => {
    const [showAll, setShowAll] = React.useState(false);
    const students: Array<{ id?: number | null; name?: string | null }> =
      Array.isArray(row?.students) ? row.students.filter((s: any) => s && (s.name || s.id)) : [];
    const enrolled = row.studentCount ?? students.length ?? 0;
    const cap = row.capacity ?? null;

    if (enrolled === 0) {
      return (
        <Chip
          size="small"
          label="No Students Assigned"
          sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: alpha("#ef4444", 0.1), color: "#b91c1c", maxWidth: "100%" }}
        />
      );
    }

    const pct = cap ? Math.min(100, Math.round((enrolled / cap) * 100)) : 100;
    const barColor = cap && enrolled >= cap ? "#ef4444" : cap && enrolled / cap >= 0.75 ? "#f59e0b" : "#22c55e";
    const MAX_VISIBLE = 3;
    const visible = showAll ? students : students.slice(0, MAX_VISIBLE);
    const remaining = Math.max(0, students.length - MAX_VISIBLE);

    return (
      <Box sx={{ width: "100%", minWidth: 0, maxWidth: 220 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.4 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>
            {enrolled}{cap ? ` / ${cap}` : ""}
          </Typography>
          {cap != null && (
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", whiteSpace: "nowrap" }}>
              {enrolled >= cap ? "Full" : `${Math.max(0, cap - enrolled)} left`}
            </Typography>
          )}
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: alpha(barColor, 0.15), "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 } }} />

        {students.length > 0 && (
          <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.4 }}>
            {visible.map((s, i) => {
              const initial = (s.name || "?").trim().charAt(0).toUpperCase() || "?";
              return (
                <StudentProfileLink
                  key={s.id ?? `${s.name}-${i}`}
                  id={s.id ?? undefined}
                  name={s.name || ""}
                  to={s.id != null ? buildStudentProfilePath(s.id) : undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                    p: "2px 4px",
                    borderRadius: "6px",
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: 1.2,
                    "&:hover": { bgcolor: alpha("#22c55e", 0.06), color: "primary.main" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: alpha("#22c55e", 0.15),
                      color: "#15803d",
                      flexShrink: 0,
                    }}
                  >
                    {initial}
                  </Avatar>
                  <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                    {s.name}
                  </Box>
                </StudentProfileLink>
              );
            })}
            {!showAll && remaining > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 0,
                  px: 0.75,
                  py: 0.15,
                  color: "primary.main",
                  "&:hover": { bgcolor: alpha("#22c55e", 0.08) },
                }}
              >
                +{remaining} more {remaining === 1 ? "student" : "students"}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const StatusBadge: React.FC<{ row: any }> = ({ row }) => {
    const m = statusMetaFor(row);
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.4, borderRadius: "999px", bgcolor: m.bg }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: m.color }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: m.text }}>{m.label}</Typography>
      </Box>
    );
  };

  // ── Row action menu (ALWAYS available; items disable with tooltips) ──
  const ActionMenuItem: React.FC<{ icon: React.ReactNode; label: string; onClick?: () => void; disabled?: boolean; disabledReason?: string; danger?: boolean; tooltip?: string }> = ({ icon, label, onClick, disabled, disabledReason, danger, tooltip }) => {
    const item = (
      <MenuItem onClick={onClick} disabled={disabled} sx={{ fontSize: 13, py: 0.9, ...(danger ? { color: "error.main" } : {}) }}>
        <ListItemIcon sx={{ color: danger ? "error.main" : "inherit" }}>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>
    );
    if (disabled && disabledReason) return <Tooltip title={disabledReason} placement="left"><span>{item}</span></Tooltip>;
    // An always-on explanatory tooltip for an enabled item (e.g. Release Class).
    // `describeChild` keeps the visible label as the accessible name (the tooltip
    // becomes an aria-description, not an overriding aria-label).
    if (tooltip) return <Tooltip title={tooltip} describeChild placement="left">{item}</Tooltip>;
    return item;
  };

  // ── Expanded row content ──
  const emptyState = (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, px: 2, textAlign: "center" }}>
      <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: alpha(theme.palette.primary.main, 0.08), display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
        <SearchOffIcon sx={{ fontSize: 34, color: theme.palette.primary.main }} />
      </Box>
      <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 0.5 }}>
        {hasDayTimeFilter ? "No class schedules match the selected days and time range." : "No class schedules match the selected filters."}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 2.5, maxWidth: 380 }}>
        Try adjusting your filters or date range, or create a new class to get started.
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
        {hasDayTimeFilter && (
          <Button variant="outlined" startIcon={<AccessTimeIcon />} onClick={clearDayTimeFilters} sx={{ textTransform: "none", borderRadius: "10px" }}>Clear Day &amp; Time Filters</Button>
        )}
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ textTransform: "none", borderRadius: "10px" }}>{hasDayTimeFilter ? "Reset All Filters" : "Adjust Filters"}</Button>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={() => {
            // Staff Portal users must never reach the Admin
            // student-first create flow. Route them straight into the
            // dedicated /staff-portal/classes/create page, which
            // locks the staff identity and hydrates Subjects & Grades
            // from the caller's own teaching assignments. Admin path
            // keeps opening the SelectStudentDialog.
            if (isStaffPortalMode) navigate("/staff-portal/classes/create");
            else setSelectStudentOpen(true);
          }}
          sx={{ textTransform: "none", borderRadius: "10px" }}
        >Create Class</Button>
      </Box>
    </Box>
  );

  // ── Desktop table row ──
  const TableRow: React.FC<{ row: any }> = ({ row }) => {
    const isExpanded = expandedId === row.id;
    const meta = statusMetaFor(row);
    return (
      <Box sx={{ borderTop: "1px solid rgba(15,23,42,0.06)", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) }, bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : selected.has(row.id) ? alpha(theme.palette.primary.main, 0.05) : "transparent" }}>
        {/* Phase-2: clicking a row opens the details drawer (fetching the
            full record on demand) instead of expanding a heavy in-row
            details block. The old expander stays wired for the row-menu
            "View expanded" path but no longer eats the row click. */}
        <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE_COLUMNS, minWidth: GRID_MIN_WIDTH, alignItems: "center", cursor: "pointer" }} onClick={() => openDetailsDrawer(row)}>
          {/* Select */}
          <Box sx={{ pl: 1 }} onClick={(e) => e.stopPropagation()}>
            <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelectRow(row.id)} />
          </Box>
          {/* Date */}
          <Box sx={{ py: 1.5, borderLeft: `3px solid ${meta.color}`, pl: 1.25 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1 }}>{formatScheduleDate(row, "MMM DD")}</Typography>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{formatScheduleDate(row, "dddd")}</Typography>
            {row.isRecurring && (
              <Tooltip title={seriesTooltip(row)}>
                <Chip icon={<EventRepeatOutlinedIcon sx={{ fontSize: "12px !important" }} />} label="Series" size="small" sx={{ mt: 0.4, height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: alpha(RECURRING_ACCENT, 0.14), color: "#b45309", "& .MuiChip-icon": { color: "#b45309" } }} />
              </Tooltip>
            )}
          </Box>
          {/* Time */}
          <Box sx={{ py: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{formatTime(row.startTime)}</Typography>
            <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>↓</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{formatTime(row.endTime)}</Typography>
          </Box>
          {/* Class */}
          <Box sx={{ py: 1.5, pr: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.className || "Class"}</Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              <Chip label={row.classType || "Regular"} size="small" sx={{ height: 17, fontSize: 9, bgcolor: alpha("#64748b", 0.1) }} />
              {row.isRecurring ? <Chip label="Recurring" size="small" sx={{ height: 17, fontSize: 9, bgcolor: alpha(RECURRING_ACCENT, 0.12), color: "#b45309" }} /> : <Chip label="One-off" size="small" sx={{ height: 17, fontSize: 9, bgcolor: alpha("#b45309", 0.1), color: "#b45309" }} />}
              <Chip label={modeLabel(row.classMode)} size="small" sx={{ height: 17, fontSize: 9, bgcolor: alpha("#0ea5e9", 0.12), color: "#0369a1" }} />
            </Box>
            {/* row.subjectName is already baked into row.className by the
                backend's compact class-label formatter — rendering it a
                second time here produced the "subject repeated below
                the header" bug on Card / Grid views. Full grade-subject
                mapping opens via the row's details drawer. */}
          </Box>
          {/* Staff */}
          <Box sx={{ py: 1.5, pr: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>{initials(row.staffName || "")}</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.staffName || "Unassigned"}</Typography>
              </Box>
            </Box>
          </Box>
          {/* Students — own lane, extra right padding separates it from Location */}
          <Box sx={{ py: 1.5, px: 2, pr: 2.5, minWidth: 0, display: "flex", alignItems: "center" }}><StudentsCell row={row} /></Box>
          {/* Location — Online classes never show "Online" here (that belongs to Mode) */}
          <Box sx={{ py: 1.5, px: 2, minWidth: 0 }}>
            {row.locationName && row.classMode !== "Online" ? (
              <Chip
                icon={<LocationOnOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                label={row.locationName}
                size="small"
                sx={{ height: 22, fontSize: 11, maxWidth: "100%", bgcolor: alpha("#64748b", 0.1), "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
              />
            ) : (
              <Typography sx={{ fontSize: 11.5, color: "text.disabled", whiteSpace: "nowrap" }}>{row.classMode === "Online" ? "Not applicable" : "—"}</Typography>
            )}
          </Box>
          {/* Mode */}
          <Box sx={{ py: 1.5, px: 2, minWidth: 0 }}>
            <Chip icon={row.classMode === "Online" ? <VideocamOutlinedIcon sx={{ fontSize: "13px !important" }} /> : <LocationOnOutlinedIcon sx={{ fontSize: "13px !important" }} />} label={modeLabel(row.classMode)} size="small" sx={{ height: 22, fontSize: 10.5, maxWidth: "100%", bgcolor: row.classMode === "Online" ? alpha("#0ea5e9", 0.12) : alpha("#8b5cf6", 0.12), color: row.classMode === "Online" ? "#0369a1" : "#6d28d9" }} />
          </Box>
          {/* Status */}
          <Box sx={{ py: 1.5, px: 2, minWidth: 0 }}><StatusBadge row={row} /></Box>
          {/* Actions */}
          <Box sx={{ py: 1.5, textAlign: "right", pr: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Actions">
              <IconButton size="small" onClick={(e) => handleOpenActionMenu(e, row)}><MoreVertIcon sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <ExpandedScheduleDetails
            row={row}
            can={{ edit: rowActionState(row).edit.on, switchStaff: rowActionState(row).switchStaff.on, release: rowActionState(row).release.on }}
            onViewDetails={() => openDetailsDrawer(row)}
            onAction={(type) => dispatchRowAction(row, type)}
          />
        </Collapse>
      </Box>
    );
  };

  // ── Mobile card ──
  const MobileCard: React.FC<{ row: any }> = ({ row }) => {
    const meta = statusMetaFor(row);
    return (
      <Box sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: "14px", p: 1.75, mb: 1.5, borderLeft: `4px solid ${meta.color}` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{formatScheduleDate(row, "MMM DD")} · <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{formatScheduleDate(row, "ddd")}</Box></Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{formatTime(row.startTime)} – {formatTime(row.endTime)}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <StatusBadge row={row} />
            <IconButton size="small" onClick={(e) => handleOpenActionMenu(e, row)}><MoreVertIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 1 }}>{row.className || "Class"}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>{initials(row.staffName || "")}</Avatar>
          <Typography sx={{ fontSize: 12.5 }}>{row.staffName || "Unassigned"}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ width: 132, flexShrink: 0 }}><StudentsCell row={row} /></Box>
        </Box>
      </Box>
    );
  };

  // ── Agenda view (grouped by date) ──
  const agendaGroups = useMemo(() => {
    const map: Record<string, any[]> = {};
    sorted.forEach((r) => {
      const k = scheduleDateKey(r);
      (map[k] = map[k] || []).push(r);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [sorted]);

  // Shell renders immediately. First fetch (`isLoading && !data`) shows a
  // skeleton inside the Body — the header, filters, KPIs, and sidebar remain
  // usable while the list warms up. Refetches with prior data flip
  // `isFetching` only and dim the existing rows (see the opacity cue below).
  const firstLoad = isLoading && !data;
  const firstLoadError = isError && !data;

  return (
    // Outer page card. `width:100%; maxWidth:100%; minWidth:0; overflow:hidden`
    // is what stops long calendar rows (or a wide table below) from pushing the
    // whole card past the main-content column — the main <Box component="main">
    // in Menu.tsx already sets minWidth:0, and this cements the chain here.
    <Box
      className={classes.card}
      sx={{
        // position: relative anchors the branded content-overlay loader
        // mounted below so filter / pagination / sort / search refetches
        // dim the whole page and show the Step Up logo loader.
        position: "relative",
        p: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        // Premium branded canvas — soft blue-green tint pulls the page out of
        // its previous flat-white feel and gives every section card a warmer
        // frame. Kept subtle so KPI + calendar content still leads the eye.
        background: "linear-gradient(180deg, #f4f8ff 0%, #f8fafc 260px, #f8fafc 100%)",
      }}
    >
      {/* ═══ 1. Hero header card ═══ */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
        <Box
          sx={{
            borderRadius: "18px",
            p: { xs: 2, md: 2.5 },
            background:
              "linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(22,163,74,0.05) 100%)",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 4px 14px rgba(15,23,42,0.04)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a" }}>Class Sessions</Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
                Manage dated class sessions, attendance, staff assignments and occurrence-level changes.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* Staff Portal hides admin-only operations (Bulk Actions + New
                  Class). Export stays available because it exports the same
                  staff-scoped list the user is already looking at. */}
              {!isStaffPortalMode && (
                <Button
                  variant="outlined"
                  startIcon={<ChecklistIcon />}
                  onClick={() => (selected.size ? clearSelection() : setSelected(new Set(pageIds)))}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    bgcolor: "#fff",
                    borderColor: alpha("#2563eb", 0.35),
                    color: "#2563eb",
                    "&:hover": { borderColor: "#2563eb", bgcolor: alpha("#2563eb", 0.06) },
                  }}
                >
                  {selected.size ? "Clear Selection" : "Bulk Actions"}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={() => exportRows(sorted)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  borderColor: alpha("#2563eb", 0.35),
                  color: "#2563eb",
                  "&:hover": { borderColor: "#2563eb", bgcolor: alpha("#2563eb", 0.06) },
                }}
              >
                Export
              </Button>
              {!isStaffPortalMode && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setSelectStudentOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: "0 6px 16px rgba(37,99,235,0.28)",
                    "&:hover": { boxShadow: "0 8px 22px rgba(37,99,235,0.36)" },
                  }}
                >
                  New Class
                </Button>
              )}
            </Box>
          </Box>

          {/* Location Colours legend — sits inside the hero card so admins
              always have one clean reference for what each colour means. */}
          <Box sx={{ mt: 2 }}>
            <LocationLegend locations={(locationData as any)?.data ?? []} includeOnline compact />
          </Box>
        </Box>

        {/* ═══ 2. KPI cards — premium tinted tiles ═══ */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 1.5, mt: 2 }}>
          {kpiCards.map((c) => (
            <Box key={c.key}
              onClick={(c as any).readOnly ? undefined : () => applyKpiFilter(c.key)}
              title={(c as any).readOnly ? `${c.label} sessions are kept out of the active schedule. This is a count for the selected range.` : undefined}
              sx={{
                p: 1.75,
                borderRadius: "14px",
                border: `1px solid ${alpha(c.color, 0.22)}`,
                // Soft tinted background derived from the KPI's accent —
                // matches the previous All Classes look; each metric is
                // recognisable at a glance without shouting.
                background: `linear-gradient(180deg, ${alpha(c.color, 0.10)} 0%, ${alpha(c.color, 0.03)} 100%)`,
                transition: "all .15s",
                position: "relative",
                overflow: "hidden",
                cursor: (c as any).readOnly ? "default" : "pointer",
                ...((c as any).readOnly ? {} : { "&:hover": { borderColor: alpha(c.color, 0.5), boxShadow: `0 6px 18px ${alpha(c.color, 0.20)}`, transform: "translateY(-2px)" } }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "10px",
                  bgcolor: alpha(c.color, 0.18), color: c.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 4px 10px ${alpha(c.color, 0.15)}`,
                  "& svg": { fontSize: 18 },
                }}>{c.icon}</Box>
              </Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: c.color, letterSpacing: "-0.02em" }}>{kpis[c.key] ?? 0}</Typography>
              <Typography sx={{ fontSize: 11.5, color: "#475569", mt: 0.5, fontWeight: 700, letterSpacing: 0.2 }}>{c.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ═══ 3. Filter card — premium, labeled, no longer sticky-transparent ═══ */}
      <Box sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{
          borderRadius: "18px",
          bgcolor: "#ffffff",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 6px 20px rgba(15,23,42,0.04)",
          p: { xs: 2, md: 2.5 },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.75, gap: 1.5, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>Filters</Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                Narrow sessions by date, staff, student, location, subject, mode, status and time.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button
                size="small"
                variant="text"
                startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
                endIcon={moreFiltersOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                onClick={() => setMoreFiltersOpen((v) => !v)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: "10px",
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                More filters{advancedFilterCount > 0 ? ` · ${advancedFilterCount}` : ""}
              </Button>
              <Button
                size="small"
                variant="text"
                startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                onClick={resetFilters}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: "10px",
                  color: "#dc2626",
                  bgcolor: alpha("#dc2626", 0.06),
                  "&:hover": { bgcolor: alpha("#dc2626", 0.12) },
                }}
              >
                Reset Filters
              </Button>
            </Stack>
          </Box>
        <Box sx={{ ...FILTER_GRID_ROW1_SX, ...FILTER_FIELD_SX }}>
          <Box sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small" placeholder="Search date, staff, student, subject…" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* minWidth on the field itself is a belt-and-braces guard so
                a future grid change can't ever collapse the date value under
                the calendar-icon adornment again. Row 1's xl template floors
                the two date cells at 170px; this keeps the input from going
                below 160px if the grid is ever restructured. */}
            <DatePicker label="From" value={startDate} onChange={setStartDate}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: { minWidth: 160 } } }} />
            <DatePicker label="To" value={endDate} onChange={setEndDate}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: { minWidth: 160 } } }} />
          </LocalizationProvider>

          {isStaffPortalMode ? (
            // Staff Portal — Staff field is locked to self. Backend enforces
            // scope regardless; the read-only chip communicates "this workspace
            // belongs to you". Selecting a different teacher must not be
            // possible here.
            <TextField
              size="small"
              label="Staff"
              value={staffOptions[0]?.label || "You"}
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 0, "& .MuiInputBase-input": { fontWeight: 600 } }}
            />
          ) : (
            <StaffMultiSelect
              staff={staffOptions}
              value={staffIds}
              onChange={setStaffIds}
              loading={optionsLoading}
              width="100%"
            />
          )}
          <Autocomplete size="small" options={studentOptions} value={selectedStudentOption} loading={optionsLoading}
            disabled={!isStaffPortalMode && !optionsLoading && studentOptions.length === 0}
            onChange={(_, opt) => setStudentFilter(opt ? String(opt.value) : "")} getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            filterOptions={(opts, s) => { const q = s.inputValue.trim().toLowerCase(); return q ? opts.filter((o) => o.search.includes(q)) : opts; }}
            noOptionsText={optionsLoading ? "Loading…" : (isStaffPortalMode ? "You have no students yet" : "No assigned students available")}
            sx={{ minWidth: 0 }}
            renderInput={(p) => (
              <TextField {...p}
                label={isStaffPortalMode ? "All My Students" : "Student"}
                placeholder={
                  isStaffPortalMode ? "All My Students"
                  : (!optionsLoading && studentOptions.length === 0 ? "No assigned students available" : "All Assigned Students")
                }
              />
            )} />

          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>{isStaffPortalMode ? "All My Locations" : "Location"}</InputLabel>
            <Select
              label={isStaffPortalMode ? "All My Locations" : "Location"}
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">{isStaffPortalMode ? "All My Locations" : "All"}</MenuItem>
              {(isStaffPortalMode
                ? ((filterOptionsData as any)?.data?.locations ?? [])
                : locationList
              ).map((l: any) => (
                <MenuItem key={l.id} value={l.name ?? l.location_name}>{l.name ?? l.location_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Grade MUST precede Subject (spec §1). Subject options are scoped
              to the selected grades (spec §2) — see scopedSubjectOptions. */}
          <GradeMultiSelect
            grades={gradeOptions.map((g) => ({ id: g.id, grade: g.name }))}
            value={selectedGradeIds}
            onChange={setSelectedGradeIds}
            width="100%"
          />
          <SubjectMultiSelect
            subjects={scopedSubjectOptions.map((s) => ({ id: s.id, subject_name: s.name }))}
            value={subjectFilter}
            onChange={setSubjectFilter}
            width="100%"
          />
        </Box>

        {/* Second filter row: Mode · Status · Enrollment · Type · Days · Start · End · Reset — view buttons stay right-aligned (never inside the filter grid).
            Phase-3: collapsed behind the "More filters" toggle in the header. */}
        <Collapse in={moreFiltersOpen} timeout="auto" unmountOnExit>
        <Box sx={{ ...FILTER_GRID_ROW2_SX, ...FILTER_FIELD_SX, mt: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Mode</InputLabel>
            <Select label="Mode" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="InPerson">In Person</MenuItem>
              <MenuItem value="Offline">In Person (Offline)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              {/* Cancelled/Rescheduled are intentionally absent: the list excludes
                  released sessions server-side, so these options could only ever
                  produce an empty result. History lives in reports/audit. */}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Enrollment</InputLabel>
            <Select label="Enrollment" value={enrollmentFilter} onChange={(e) => setEnrollmentFilter(e.target.value as EnrollmentFilter)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending (0)</MenuItem>
              <MenuItem value="enrolled">Enrolled</MenuItem>
              <MenuItem value="full">Full</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Class Type</InputLabel>
            <Select label="Class Type" value={recurringFilter} onChange={(e) => setRecurringFilter(e.target.value as RecurringFilter)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="recurring">Recurring</MenuItem>
              <MenuItem value="oneoff">One-off</MenuItem>
            </Select>
          </FormControl>

          {/* Days (multi-select, Match Any) · Start ≥ · End ≤ — same grid cells as
              every other control, so they read as part of the toolbar rather than
              as an afterthought bolted onto the end. */}
          <DayMultiSelect value={selectedDays} onChange={setSelectedDays} width="100%" />
          <Box sx={{ minWidth: 0 }}>
            <PremiumTimePicker
              label="Start Time"
              value={startTime}
              onChange={setStartTime}
              placeholder="Any Start Time"
              minuteStep={5}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <PremiumTimePicker
              label="End Time"
              value={endTime}
              onChange={setEndTime}
              placeholder="Any End Time"
              minuteStep={5}
              error={timeRangeInvalid}
              helperText={timeRangeInvalid ? "End Time must be later than Start Time." : undefined}
            />
          </Box>

          {/* Reset closes the second row — real button, aligned with every
              other control's 44px height, filling its own grid cell so it can
              never look like a link squeezed beside End Time. */}
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetFilters}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              borderColor: "rgba(15,23,42,0.18)",
              borderRadius: "10px",
              height: 44,
              width: "100%",
              whiteSpace: "nowrap",
              minWidth: 0,
              fontWeight: 600,
            }}
          >
            Reset Filters
          </Button>
        </Box>
        </Collapse>

        {/* View modes — premium segmented control with icon + label so admins
            recognise each view at a glance. Right-aligned so it never
            compresses the filter fields. */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setViewMode(v)}
            aria-label="Calendar view mode"
            sx={{
              bgcolor: alpha("#2563eb", 0.06),
              borderRadius: "12px",
              p: "3px",
              gap: "2px",
              "& .MuiToggleButton-root": {
                border: "none !important",
                borderRadius: "10px !important",
                px: { xs: 1, sm: 1.75 },
                py: 0.75,
                gap: 0.75,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: "#475569",
                "&:hover": { bgcolor: alpha("#2563eb", 0.08) },
                "&.Mui-selected": {
                  bgcolor: "#fff",
                  color: "#1d4ed8",
                  boxShadow: "0 1px 4px rgba(15,23,42,0.10)",
                  "&:hover": { bgcolor: "#fff" },
                },
              },
            }}
          >
            <Tooltip title="Week (grouped by day)"><ToggleButton value="week_timetable" aria-label="Week timetable"><ViewAgendaOutlinedIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Week</Box></ToggleButton></Tooltip>
            <Tooltip title="Day"><ToggleButton value="day" aria-label="Day view"><ViewDayOutlinedIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Day</Box></ToggleButton></Tooltip>
            <Tooltip title="Card list"><ToggleButton value="list" aria-label="Card list view"><TableRowsIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Card</Box></ToggleButton></Tooltip>
            <Tooltip title="Agenda"><ToggleButton value="agenda" aria-label="Agenda view"><ViewWeekOutlinedIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Agenda</Box></ToggleButton></Tooltip>
            <Tooltip title="Week (grid)"><ToggleButton value="week" aria-label="Week grid view"><ViewAgendaOutlinedIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Grid</Box></ToggleButton></Tooltip>
            <Tooltip title="Month"><ToggleButton value="calendar" aria-label="Month view"><CalendarMonthIcon sx={{ fontSize: 16 }} /><Box sx={{ display: { xs: "none", sm: "inline" } }}>Month</Box></ToggleButton></Tooltip>
          </ToggleButtonGroup>
        </Box>

        {activeChips.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.5, pt: 1.5, borderTop: "1px dashed rgba(15,23,42,0.08)", alignItems: "center" }}>
            <Typography sx={{ fontSize: 11.5, color: "#334155", fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", mr: 0.25 }}>Active filters</Typography>
            {activeChips.map((c, i) => (
              <Chip
                key={i}
                label={c.label}
                size="small"
                onDelete={c.onDelete}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 26,
                  fontSize: 11.5,
                  fontWeight: 700,
                  bgcolor: alpha("#2563eb", 0.08),
                  color: "#1d4ed8",
                  border: `1px solid ${alpha("#2563eb", 0.18)}`,
                  "& .MuiChip-deleteIcon": { color: alpha("#1d4ed8", 0.7), "&:hover": { color: "#dc2626" } },
                }}
              />
            ))}
          </Box>
        )}
        </Box>
      </Box>

      {/* ═══ Bulk action bar ═══ */}
      {selected.size > 0 && (
        <Box sx={{ mx: { xs: 2, md: 3 }, mt: 1.5, p: 1.25, borderRadius: "12px", bgcolor: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, ml: 0.5 }}>{selected.size} selected</Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Button size="small" startIcon={<FileDownloadIcon />} onClick={() => exportRows(selectedRows)} sx={{ textTransform: "none" }}>Export</Button>
          <Button size="small" color="error" startIcon={<EventBusyIcon />} onClick={handleBulkCancel} sx={{ textTransform: "none" }}>Cancel Sessions</Button>
          <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => setBulkSwitchOpen(true)} sx={{ textTransform: "none" }}>Switch Staff</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={clearSelection} sx={{ textTransform: "none", color: "text.secondary" }}>Clear</Button>
        </Box>
      )}

      {/* ═══ Body ═══ */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        {/* isLoading is gated at the top of the component so the shell never
            renders without data. What's left here is:
             - isFetching (filter/page change with prior data) → keep the
               previous rows visible; the `isFetching ? 0.75 : 1` opacity
               applied downstream is the local loading cue.
             - isError WITH prior data → a compact inline retry banner
               above the last-known rows. */}
        {isError && data && (
          <Box role="alert" sx={{ mb: 2, p: 1.5, borderRadius: "10px", bgcolor: alpha("#dc2626", 0.06), border: "1px solid rgba(220,38,38,0.15)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12.5, color: "#b91c1c", fontWeight: 700 }}>
              Couldn't refresh — showing the last successful result.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "8px", color: "#b91c1c", borderColor: "rgba(220,38,38,0.35)" }}>
              Retry
            </Button>
          </Box>
        )}
        {firstLoadError ? (
          <Box role="alert" sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.75 }}>
              Couldn't load class sessions
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
              The server didn't respond. Check your connection and try again.
            </Typography>
            <Button variant="contained" onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "10px" }}>
              Try again
            </Button>
          </Box>
        ) : firstLoad ? (
          <Box sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: "14px", overflow: "hidden" }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.25, borderBottom: i < 7 ? "1px solid rgba(15,23,42,0.06)" : "none" }}>
                <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: "4px" }} />
                <Skeleton variant="text" width={90} />
                <Skeleton variant="text" width={70} />
                <Skeleton variant="text" width="22%" />
                <Skeleton variant="text" width="12%" />
                <Skeleton variant="text" width={60} />
                <Skeleton variant="text" width={100} />
                <Skeleton variant="rectangular" width={56} height={20} sx={{ borderRadius: "10px" }} />
                <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: "10px" }} />
              </Box>
            ))}
          </Box>
        ) : sorted.length === 0 ? (
          emptyState
        ) : viewMode === "week_timetable" ? (
          <WeekTimetableView
            rows={sorted}
            weekStart={mondayOf(focusDate)}
            onPrev={() => setFocusDate((d) => d.subtract(7, "day"))}
            onNext={() => setFocusDate((d) => d.add(7, "day"))}
            onToday={() => setFocusDate(dayjs())}
            onPickDate={(d) => setFocusDate(d)}
            onEventClick={(e, row) => handleOpenActionMenu(e, row)}
          />
        ) : viewMode === "list" ? (
          isMobileRes ? (
            <Box>{paged.map((row) => <MobileCard key={row.id} row={row} />)}</Box>
          ) : (
            <Box sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: "14px", overflow: "hidden" }}>
              {/* Horizontal scroll container — below GRID_MIN_WIDTH the whole grid
                  scrolls as one unit so Students/Location stay separated instead of
                  merging. Header and rows share the SAME grid template + minWidth. */}
              <Box sx={{ overflowX: "auto" }}>
                {/* Header */}
                <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE_COLUMNS, minWidth: GRID_MIN_WIDTH, alignItems: "center", bgcolor: alpha("#0f172a", 0.02) }}>
                  <Box sx={{ pl: 1 }}><Checkbox size="small" indeterminate={somePageSelected && !allPageSelected} checked={allPageSelected} onChange={toggleSelectAllPage} /></Box>
                  <SortHead label="Date" sortKey="date" />
                  <SortHead label="Time" sortKey="time" />
                  <SortHead label="Class" sortKey="class" />
                  <SortHead label="Staff" sortKey="staff" />
                  <SortHead label="Students" sortKey="enrollment" />
                  <SortHead label="Location" />
                  <SortHead label="Mode" />
                  <SortHead label="Status" sortKey="status" />
                  <SortHead label="" />
                </Box>
                {paged.map((row) => <TableRow key={row.id} row={row} />)}
              </Box>
            </Box>
          )
        ) : viewMode === "agenda" ? (
          <Box>
            {agendaGroups.map(([date, rows]) => (
              <Box key={date} sx={{ mb: 2 }}>
                <Box sx={{ position: "sticky", top: 60, zIndex: 1, py: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{dayjs(date).format("dddd, MMM D")}</Typography>
                  <Chip label={`${rows.length}`} size="small" sx={{ height: 18, fontSize: 10 }} />
                </Box>
                {rows.map((row) => <MobileCard key={row.id} row={row} />)}
              </Box>
            ))}
          </Box>
        ) : viewMode === "day" ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <IconButton size="small" onClick={() => setFocusDate((d) => d.subtract(1, "day"))}><ChevronLeftIcon /></IconButton>
              <Button size="small" variant="outlined" onClick={() => setFocusDate(dayjs())} sx={{ textTransform: "none", borderRadius: "8px" }}>Today</Button>
              <IconButton size="small" onClick={() => setFocusDate((d) => d.add(1, "day"))}><ChevronRightIcon /></IconButton>
              <Typography sx={{ fontSize: 15, fontWeight: 800, ml: 1 }}>{focusDate.format("dddd, MMMM D, YYYY")}</Typography>
            </Box>
            <DayView rows={sorted} date={focusDate} onEventClick={(e, row) => handleOpenActionMenu(e, row)} />
          </Box>
        ) : viewMode === "week" ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <IconButton size="small" onClick={() => setFocusDate((d) => d.subtract(7, "day"))}><ChevronLeftIcon /></IconButton>
              <Button size="small" variant="outlined" onClick={() => setFocusDate(dayjs())} sx={{ textTransform: "none", borderRadius: "8px" }}>Today</Button>
              <IconButton size="small" onClick={() => setFocusDate((d) => d.add(7, "day"))}><ChevronRightIcon /></IconButton>
              <Typography sx={{ fontSize: 15, fontWeight: 800, ml: 1 }}>
                {mondayOf(focusDate).format("MMM D")} – {mondayOf(focusDate).add(6, "day").format("MMM D, YYYY")}
              </Typography>
            </Box>
            <WeekView rows={sorted} weekStart={mondayOf(focusDate)} onEventClick={(e, row) => handleOpenActionMenu(e, row)} />
          </Box>
        ) : (
          // ── Calendar (month) ──────────────────────────────────────────────
          // Prev / Today / Next drive `visibleMonth` (and the fetch range) but
          // never the view mode. The +N more popover shows overflow events using
          // the same MonthEventCard so behaviour stays consistent.
          //
          // Outer Box enforces min-width:0 + max-width:100% so the grid never
          // pushes past the main-content column when a cell's intrinsic content
          // (long grade/subject text) is wider than its `1fr` share. The child
          // grid uses `minmax(0, 1fr)` for the same reason at the track level.
          <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
              <Tooltip title="Previous month">
                <IconButton size="small" aria-label="Previous month" onClick={() => goToMonth(visibleMonth.subtract(1, "month"))}><ChevronLeftIcon /></IconButton>
              </Tooltip>
              <Button size="small" variant="outlined" onClick={() => goToMonth(dayjs())} sx={{ textTransform: "none", borderRadius: "8px" }}>Today</Button>
              <Tooltip title="Next month">
                <IconButton size="small" aria-label="Next month" onClick={() => goToMonth(visibleMonth.add(1, "month"))}><ChevronRightIcon /></IconButton>
              </Tooltip>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{visibleMonth.format("MMMM YYYY")}</Typography>
              </Box>
              <LegendBar rows={sorted} />
            </Box>
            <Box
              ref={monthGridRef}
              sx={{
                display: "grid",
                // minmax(0, 1fr) — NOT 1fr. Grid tracks default their minimum to
                // `auto` (intrinsic min-content), so a cell with a long, non-
                // wrapping event card would force its column wider than 1fr and
                // push Fri/Sat past the container. `0` as the minimum lets the
                // column shrink to fit, and the card itself handles ellipsis.
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: 1,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <Box key={d} sx={{ textAlign: "center", fontWeight: 700, fontSize: 12, py: 1, color: "text.secondary", minWidth: 0 }}>{d}</Box>
              ))}
              {Array.from({ length: 42 }, (_, i) => {
                const calStart = visibleMonth.startOf("month").startOf("week");
                const cellDate = calStart.add(i, "day");
                const day = sorted.filter((s) => scheduleDateKey(s) === cellDate.format("YYYY-MM-DD"));
                const inMonth = cellDate.month() === visibleMonth.month();
                const isToday = cellDate.isSame(dayjs(), "day");
                const shown = day.slice(0, monthLimits.maxEventsPerCell);
                const overflow = day.length - shown.length;
                return (
                  <Box
                    key={i}
                    sx={{
                      border: "1px solid",
                      borderColor: isToday ? alpha(theme.palette.primary.main, 0.5) : "divider",
                      borderRadius: "8px",
                      p: 0.5,
                      // Restored to the previous compact row height so the
                      // whole month stays visible without huge blank cells.
                      // Card is small enough (~80px) to fit; when multiple
                      // events pile up, the existing +N more link handles it.
                      minHeight: 108,
                      bgcolor: inMonth ? "transparent" : alpha("#0f172a", 0.02),
                      // Same story as the grid tracks — grid CELLS also need
                      // minWidth:0 so their content can't push the track wider
                      // than 1fr. overflow:hidden keeps event ellipsis honest.
                      minWidth: 0,
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: inMonth ? (isToday ? theme.palette.primary.main : "text.primary") : "text.disabled",
                      }}
                    >
                      {cellDate.date()}
                    </Typography>
                    {shown.map((s) => (
                      <MonthEventCard key={s.id} row={s} onClick={openDetailsDrawer} compact density={monthLimits.density} />
                    ))}
                    {overflow > 0 && (
                      <Box
                        component="button"
                        onClick={(e) => setMorePop({ anchor: e.currentTarget as HTMLElement, rows: day })}
                        aria-label={`Show ${overflow} more event${overflow === 1 ? "" : "s"} on ${cellDate.format("MMMM D")}`}
                        sx={{
                          mt: 0.35,
                          border: "none",
                          background: "transparent",
                          color: "text.secondary",
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: "pointer",
                          p: 0,
                          textAlign: "left",
                          width: "100%",
                          "&:hover": { textDecoration: "underline" },
                          "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1, borderRadius: 4 },
                        }}
                      >
                        +{overflow} more
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Popover
              open={Boolean(morePop)}
              anchorEl={morePop?.anchor ?? null}
              onClose={() => setMorePop(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 1, minWidth: 220, maxWidth: 320 }}>
                {(morePop?.rows || []).map((s) => (
                  // Popover always uses full density — space isn't at a premium
                  // here and users expect to see everything they missed.
                  <MonthEventCard
                    key={s.id}
                    row={s}
                    onClick={(r) => { setMorePop(null); openDetailsDrawer(r); }}
                    density="full"
                  />
                ))}
              </Box>
            </Popover>
          </Box>
        )}

        {/* Pagination (list view) — driven by server pagination metadata,
            not by the client-filtered `sorted.length`, so paging steps the
            backend query rather than slicing an in-memory copy. */}
        {viewMode === "list" && pagination.total > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, pt: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Showing {pagination.from ?? 0}–{pagination.to ?? 0} of {pagination.total} schedules
              </Typography>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Rows</InputLabel>
                <Select label="Rows" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination count={pagination.total} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </Box>

      {/* Delete popup */}
      {/* ═══ Row Action Menu (ALWAYS available) ═══ */}
      {(() => {
        const g = actionRow ? rowActionState(actionRow) : null;
        return (
          <Menu
            open={Boolean(menuPos)}
            onClose={handleCloseActionMenu}
            anchorReference="anchorPosition"
            anchorPosition={menuPos ?? undefined}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            slotProps={{ paper: { sx: { minWidth: 214, borderRadius: "12px", boxShadow: "0 8px 30px rgba(15,23,42,0.18)" } } }}
            sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
          >
            <ActionMenuItem icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} label="View Details" onClick={() => { const r = actionRow; handleCloseActionMenu(); if (r) openDetailsDrawer(r); }} />
            <ActionMenuItem icon={<EditSharpIcon sx={{ fontSize: 18 }} />} label="Edit" disabled={!g?.edit.on} disabledReason={g?.edit.why} onClick={() => { const r = actionRow; handleCloseActionMenu(); if (r?.id) navigate(`/class-sessions/${r.id}/edit`); }} />
            <ActionMenuItem icon={<FactCheckOutlinedIcon sx={{ fontSize: 18 }} />} label="Attendance" disabled={!g?.attendance.on} disabledReason={g?.attendance.why} onClick={() => { const r = actionRow; handleCloseActionMenu(); navigate(`/attendance/create?date=${r?.scheduledDate ?? ""}`); }} />
            <ActionMenuItem icon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 18 }} />} label="Assign Student" disabled={!g?.assignStudent.on} disabledReason={g?.assignStudent.why} onClick={() => { handleCloseActionMenu(); setAssignOpen(true); }} />
            {/* View / Edit / Attendance / Assign above the divider; the actions that
                CHANGE the session below it. View Series and Duplicate were removed
                (not disabled) along with the student-level Remove Student, which is
                now surfaced as Turn Out. */}
            <Divider />
            <ActionMenuItem icon={<EventRepeatOutlinedIcon sx={{ fontSize: 18 }} />} label="Schedule Makeup" disabled={!g?.scheduleMakeup.on} disabledReason={g?.scheduleMakeup.why} onClick={() => { const r = actionRow; handleCloseActionMenu(); if (r) dispatchRowAction(r, "scheduleMakeup"); }} />
            <ActionMenuItem icon={<SwapHorizIcon sx={{ fontSize: 18 }} />} label="Switch Staff" disabled={!g?.switchStaff.on} disabledReason={g?.switchStaff.why} onClick={handleOpenSwitchDialog} />
            <ActionMenuItem icon={<PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />} label="Turn Out" tooltip="Remove one student from this session, optionally with a makeup. The class stays scheduled for everyone else." disabled={!g?.turnOut.on} disabledReason={g?.turnOut.why} onClick={() => { const r = actionRow; handleCloseActionMenu(); if (r) dispatchRowAction(r, "turnOut"); }} />
            <ActionMenuItem icon={<EventBusyIcon sx={{ fontSize: 18 }} />} label="Release Class" tooltip="Cancel this occurrence, remove future occurrences, or schedule a makeup class." disabled={!g?.release.on} disabledReason={g?.release.why} onClick={() => handleOpenReleaseDialog("remove")} />
          </Menu>
        );
      })()}

      {/* ═══ Switch Staff ═══
          Extracted to its own component: it now sources candidates from the
          replacement-staff endpoint (qualified + genuinely free only) instead of
          listing all staff, and requires a reason. */}
      <SwitchStaffDialog
        open={switchDialogOpen}
        row={actionRow}
        onClose={() => setSwitchDialogOpen(false)}
        onSuccess={refreshAfterMutation}
      />

      {/* ═══ Release Class Dialog ═══ */}
      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Release Class</DialogTitle>
        <DialogContent>
          {actionRow && (
            <Box sx={{ mb: 2, p: 2, borderRadius: "10px", bgcolor: alpha("#0f172a", 0.02), border: "1px solid rgba(15,23,42,0.08)" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatScheduleDate(actionRow, "MMM D, YYYY")} — {formatTime(actionRow.startTime)} to {formatTime(actionRow.endTime)}</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{actionRow.staffName || "Unassigned"} · {actionRow.className}</Typography>
            </Box>
          )}
          {/* This is a CLASS-level cancellation — never a single-student removal. */}
          <Box sx={{ mb: 2, p: 1.5, borderRadius: "8px", bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}` }}>
            <Typography sx={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
              This action affects the complete class session and all assigned students. To remove only one student, use <b>Remove Student</b>.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Scope</Typography>
          <RadioGroup value={releaseType} onChange={(e) => setReleaseType(e.target.value as any)} sx={{ mb: 1.5 }}>
            <FormControlLabel value="remove" control={<Radio size="small" />} label={<Box><Typography sx={{ fontSize: 13, fontWeight: 500 }}>This occurrence only</Typography><Typography sx={{ fontSize: 11, color: "text.secondary" }}>Cancels only this class session. Future sessions remain unchanged.</Typography></Box>} />
            <FormControlLabel value="remove_future" control={<Radio size="small" />} label={<Box><Typography sx={{ fontSize: 13, fontWeight: 500 }}>This and future occurrences</Typography><Typography sx={{ fontSize: 11, color: "text.secondary" }}>Cancels this class and all eligible future sessions in the recurring series. Past, completed, invoiced, makeup and overridden sessions remain unchanged.</Typography></Box>} />
            <FormControlLabel value="makeup" control={<Radio size="small" />} label={<Box><Typography sx={{ fontSize: 13, fontWeight: 500 }}>Cancel and schedule makeup</Typography><Typography sx={{ fontSize: 11, color: "text.secondary" }}>Cancels this occurrence and creates or joins a replacement makeup class.</Typography></Box>} />
          </RadioGroup>

          {/* Scope-specific consequence notice */}
          {releaseType === "remove_future" && (
            <Typography sx={{ fontSize: 11.5, color: "#b45309", mb: 1.5 }}>
              This will cancel this and all eligible future sessions in the series{actionRow ? ` from ${formatScheduleDate(actionRow, "MMM D, YYYY")} onward` : ""}.
            </Typography>
          )}
          {releaseType === "makeup" && (
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 1.5 }}>
              This will cancel the original occurrence and create or join a replacement makeup class.
            </Typography>
          )}

          {/* Required reason (with "Other" → typed text) + optional notes. */}
          <TextField select label="Reason *" size="small" fullWidth value={releaseReason}
            onChange={(e) => { setReleaseReason(e.target.value); if (e.target.value !== "Other") setReleaseReasonOther(""); }}
            error={releaseTried && releaseReason === ""}
            helperText={releaseTried && releaseReason === "" ? "Please select a reason." : " "} sx={{ mb: 1.5 }}>
            <MenuItem value="">Select a reason…</MenuItem>
            {RELEASE_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          {releaseReason === "Other" && (
            <TextField label="Specify reason *" size="small" fullWidth value={releaseReasonOther}
              onChange={(e) => setReleaseReasonOther(e.target.value)} inputProps={{ maxLength: 500 }}
              error={releaseTried && releaseReasonOther.trim() === ""}
              helperText={releaseTried && releaseReasonOther.trim() === "" ? "Please enter the reason." : " "} sx={{ mb: 1.5 }} />
          )}
          <TextField label="Notes (optional)" size="small" fullWidth multiline rows={2} value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)} sx={{ mb: 2 }} />
          {releaseType === "makeup" && (
            <Box sx={{ p: 2, borderRadius: "8px", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
                <RepeatIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom" }} /> Makeup Class Details
              </Typography>
              {/* Makeup session date — shared AppDatePicker (date-only YYYY-MM-DD) */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <AppDatePicker label="Makeup Date *" size="small" fullWidth value={makeupDate} onChange={(v: string) => { setMakeupDate(v); setMakeupStaffId(""); }} />
                <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                  <PremiumTimePicker label="Start Time" value={makeupStartTime}
                    error={!!makeupEndTime && !!makeupStartTime && makeupEndTime <= makeupStartTime}
                    onChange={(v) => {
                      // Preserve the original duration when a valid one exists.
                      const dur = makeupStartTime && makeupEndTime ? (toMin(makeupEndTime) - toMin(makeupStartTime)) : 0;
                      setMakeupStartTime(v);
                      if (v && dur > 0) setMakeupEndTime(minToHHmm(toMin(v) + dur));
                      setMakeupStaffId("");
                    }} />
                  <PremiumTimePicker label="End Time" value={makeupEndTime}
                    error={!!makeupEndTime && !!makeupStartTime && makeupEndTime <= makeupStartTime}
                    helperText={makeupEndTime && makeupStartTime && makeupEndTime <= makeupStartTime ? "End time must be later than start time." : undefined}
                    onChange={(v) => { setMakeupEndTime(v); setMakeupStaffId(""); }} />
                </Box>
                <ReplacementStaffPicker
                  occurrenceId={actionRow?.id}
                  makeupDate={makeupDate}
                  startTime={makeupStartTime}
                  endTime={makeupEndTime}
                  studyMode={actionRow?.classMode === "Online" ? "online" : "in_person"}
                  locationId={actionRow?.locationId ?? null}
                  value={makeupStaffId}
                  onChange={setMakeupStaffId}
                  onPickAlternative={({ staffId, start, end }) => { setMakeupStartTime(start); setMakeupEndTime(end); setMakeupStaffId(String(staffId)); }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {/* Standard dialog-close button (not the removed row "Cancel" action). */}
          <Button onClick={() => setReleaseDialogOpen(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color={releaseType === "makeup" ? "primary" : "error"} onClick={handleReleaseClass}
            disabled={releaseLoading || !releaseReasonValid || (releaseType === "makeup" && (!makeupDate || !makeupStartTime || !makeupEndTime || makeupEndTime <= makeupStartTime || !makeupStaffId))}
            startIcon={releaseLoading ? <CircularProgress size={16} color="inherit" /> : releaseType === "makeup" ? <RepeatIcon /> : <EventBusyIcon />}>
            {releaseLoading ? "Processing..." : releaseType === "makeup" ? "Release & Schedule Makeup" : releaseType === "remove_future" ? "Release These Classes" : "Release This Class"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ View Details drawer ═══ */}
      <ScheduleDetailsDrawer
        open={detailsOpen}
        occurrenceId={detailsRow?.id ?? null}
        fallbackRow={detailsRow}
        can={detailsRow ? (() => {
          const g = rowActionState(detailsRow);
          // Pass the full {on, why} gates so disabled quick actions can explain why.
          return {
            edit: g.edit,
            attendance: g.attendance,
            assignStudent: g.assignStudent,
            switchStaff: g.switchStaff,
            release: g.release,
            turnOut: g.turnOut,
            scheduleMakeup: g.scheduleMakeup,
            viewStudent: true,
            viewStaff: true,
          };
        })() : {}}
        onClose={() => setDetailsOpen(false)}
        onAction={(type, payload) => {
          if (type === "viewStudent" && payload) { navigate(buildStudentProfilePath(payload)); return; }
          if (type === "viewStaff" && payload) { navigate(`/team-history/${payload}`); return; }
          if (detailsRow) { setDetailsOpen(false); dispatchRowAction(detailsRow, type); }
        }}
      />

      {/* ═══ Remove Student / Schedule Makeup ═══ */}
      <MakeupWorkflow
        open={makeupOpen}
        row={actionRow}
        initialMode={makeupMode}
        preselectStudentId={studentFilter || null}
        onClose={() => setMakeupOpen(false)}
      />

      {/* ═══ Duplicate / Assign Student / Bulk Switch ═══ */}
      <DuplicateDialog open={duplicateOpen} row={actionRow} staffList={staffList} onClose={() => setDuplicateOpen(false)} />
      <AssignStudentDialog open={assignOpen} row={actionRow} canOverrideGrade={canEditClasses} onClose={() => setAssignOpen(false)} />
      <BulkSwitchStaffDialog open={bulkSwitchOpen} rows={selectedRows} staffList={staffList} onClose={() => setBulkSwitchOpen(false)} onSuccess={clearSelection} />

      {/* Student-first "New Class" entry point. Replaces the generic Create
          Class popup on the Class Sessions page. Passes the current URL back
          so the Assign flow can offer a Return-to-sessions escape hatch. */}
      <SelectStudentDialog
        open={selectStudentOpen}
        onClose={() => setSelectStudentOpen(false)}
        returnUrl={`${window.location.pathname}${window.location.search}`}
      />

      {/* Branded Step Up Academy content-overlay for post-initial-load
          refetches — filter / search / sort / pagination / view-mode
          switches show the same logo loader as the initial route load
          instead of only the thin top progress bar. isLoading gates the
          full-page Loader above; this overlay handles everything after. */}
      <PageOverlayLoader visible={isFetching && !isLoading} size="large" />
    </Box>
  );
};

export default Index;
